import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Snackbar,
  Avatar,
  Stack,
  Tabs,
  Tab,
  InputAdornment,
  Link,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  AutoAwesome as AutomationIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import AutomationService from "../services/AutomationService";
import LeadService from "../services/LeadService";
import appConfig from "../shared/js/app-config";
import ConversationService from "../services/ConversationService";
import TagService from "../services/TagService";
import MessageTemplateService from "../services/MessageTemplateService";
import servicesService from "../services/ServicesService";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import FormFlowBuilder from "./FormFlowBuilder";
import ChatbotLibraryPanel from "../components/ChatbotLibraryPanel";

const SHOPIFY_PAYLOAD_FIELDS = [
  { value: "customer.first_name", label: "Customer first name" },
  { value: "customer.last_name", label: "Customer last name" },
  { value: "customer.email", label: "Customer email" },
  { value: "name", label: "Order name (#1021)" },
  { value: "order_number", label: "Order number (1021)" },
  { value: "total_price", label: "Order total" },
  { value: "financial_status", label: "Payment status" },
  { value: "fulfillment_status", label: "Fulfillment status" },
  { value: "billing_address.first_name", label: "Billing first name" },
  { value: "billing_address.phone", label: "Billing phone" },
  { value: "shipping_address.city", label: "Shipping city" },
  { value: "fulfillments[0].tracking_number", label: "Tracking number" },
];

const ESTORE_PAYLOAD_FIELDS = [
  { value: "customer.first_name", label: "Customer first name ({{1}})" },
  { value: "customer.name", label: "Customer full name" },
  { value: "name", label: "Order name / # ({{2}})" },
  { value: "status", label: "Order status ({{3}})" },
  { value: "payment_status", label: "Payment status" },
  { value: "id", label: "Order ID" },
  { value: "estore_store_id", label: "Store ID" },
  { value: "estore_payment_reference", label: "Payment reference" },
];

const SHOPIFY_PLATFORM_EVENTS = [
  { value: "orders/create", label: "Order created" },
  { value: "orders/paid", label: "Order paid" },
  { value: "orders/cancelled", label: "Order cancelled" },
  { value: "orders/fulfilled", label: "Order fulfilled" },
  { value: "fulfillments/create", label: "Fulfillment created" },
  { value: "fulfillments/update", label: "Fulfillment updated" },
  { value: "refunds/create", label: "Refund issued" },
  { value: "shopify/checkouts/abandoned", label: "Abandoned cart" },
];

const ABANDONED_SHOPIFY_PAYLOAD_FIELDS = [
  { value: "customer.first_name", label: "Customer first name" },
  { value: "customer.name", label: "Customer full name" },
  { value: "cart_total", label: "Cart total" },
  { value: "abandoned_checkout_url", label: "Recovery checkout URL" },
  { value: "line_items_count", label: "Line items count" },
];

const ABANDONED_ESTORE_PAYLOAD_FIELDS = [
  { value: "customer.first_name", label: "Customer first name" },
  { value: "customer.name", label: "Customer full name" },
  { value: "customer.phone", label: "Customer phone" },
  { value: "cart_total", label: "Cart total" },
  { value: "recovery_url", label: "Store cart URL" },
  { value: "line_items_count", label: "Line items count" },
  { value: "total_quantity", label: "Total item quantity" },
  { value: "items_summary", label: "Items summary (names)" },
  { value: "estore_store_id", label: "Store ID" },
];

const LEAD_FOLLOWUP_EVENT = "leads/stage_followup";
const LEAD_STAGE_FOLLOWUP_TRIGGER = "lead_stage_followup";
const PHONE_CALL_INCOMING_TRIGGER = "phone_call_incoming";
const PHONE_CALL_ENDED_TRIGGER = "phone_call_ended";

const LEAD_PAYLOAD_FIELDS = [
  { value: "lead_title", label: "Lead title" },
  { value: "lead_description", label: "Lead description" },
  { value: "stage_name", label: "Stage name" },
  { value: "customer.name", label: "Contact name" },
  { value: "customer.first_name", label: "Contact first name" },
  { value: "customer.phone", label: "Contact phone" },
  { value: "customer.email", label: "Contact email" },
];

const PHONE_CALL_PAYLOAD_FIELDS = [
  { value: "customer.name", label: "Contact name" },
  { value: "customer.first_name", label: "Contact first name" },
  { value: "customer.phone", label: "Contact phone" },
  { value: "call_type", label: "Call type (incoming/outgoing/missed)" },
  { value: "duration_seconds", label: "Call duration (seconds)" },
  { value: "lead_title", label: "Lead title" },
];

const ABANDONED_CART_EVENT_VALUES = new Set([
  "shopify/checkouts/abandoned",
  "estore/cart/abandoned",
  "woocommerce/cart/abandoned",
]);

function isAbandonedCartPlatformEvent(eventType) {
  return ABANDONED_CART_EVENT_VALUES.has(String(eventType || ""));
}

function isLeadFollowupPlatformEvent(eventType) {
  return String(eventType || "") === LEAD_FOLLOWUP_EVENT;
}

function isLeadStageFollowupTrigger(triggerType) {
  return String(triggerType || "") === LEAD_STAGE_FOLLOWUP_TRIGGER;
}

function isPhoneCallIncomingTrigger(triggerType) {
  return String(triggerType || "") === PHONE_CALL_INCOMING_TRIGGER;
}

function isPhoneCallEndedTrigger(triggerType) {
  return String(triggerType || "") === PHONE_CALL_ENDED_TRIGGER;
}

function isPhoneCallAutomationTrigger(triggerType) {
  return isPhoneCallIncomingTrigger(triggerType) || isPhoneCallEndedTrigger(triggerType);
}

function isImageReceivedTrigger(triggerType) {
  return String(triggerType || "") === "image_received";
}

function isOrderReceivedTrigger(triggerType) {
  return String(triggerType || "") === "order_received";
}

function isInboundPayloadTriggerWithoutKeywords(triggerType) {
  return isImageReceivedTrigger(triggerType) || isOrderReceivedTrigger(triggerType);
}

function isMessageReceivedTrigger(triggerType) {
  return String(triggerType || "") === "message_received";
}

/** @returns {string|null} */
function getAutomationTriggerOverlapHint({
  triggerType,
  rules,
  editingRuleId,
}) {
  const others = (rules || []).filter((r) => r.id !== editingRuleId && r.enabled !== false);
  if (isMessageReceivedTrigger(triggerType)) {
    const hasImageRule = others.some((r) => isImageReceivedTrigger(r.trigger_type));
    const hasOrderRule = others.some((r) => isOrderReceivedTrigger(r.trigger_type));
    if (hasImageRule && hasOrderRule) {
      return "Dedicated image and order rules are enabled — this broad trigger will not run for photos or catalog orders (only text and other message types).";
    }
    if (hasImageRule) {
      return "A Customer sends image rule is enabled — this broad trigger will not run for inbound photos.";
    }
    if (hasOrderRule) {
      return "A Customer sends catalog order rule is enabled — this broad trigger will not run for catalog orders.";
    }
    return "Fires on every inbound message type. Prefer Customer sends image or Customer sends catalog order for those cases.";
  }
  if (isImageReceivedTrigger(triggerType)) {
    const broadRules = others.filter((r) => isMessageReceivedTrigger(r.trigger_type));
    if (broadRules.length > 0) {
      return "When enabled, inbound images skip broad Message received rules so this rule can handle photos.";
    }
  }
  if (isOrderReceivedTrigger(triggerType)) {
    const broadRules = others.filter((r) => isMessageReceivedTrigger(r.trigger_type));
    if (broadRules.length > 0) {
      return "When enabled, catalog orders skip broad Message received rules so this rule can handle orders.";
    }
  }
  return null;
}

function getDefaultPhoneCallEndedFilters(callType = "") {
  return {
    logic: "all",
    conditions: callType
      ? [{ field: "call_type", operator: "equals", value: callType }]
      : [],
  };
}

function isStageFollowUpScheduleEvent(eventType) {
  return isAbandonedCartPlatformEvent(eventType);
}

function getDefaultLeadStageFollowupFilters(stageId = "") {
  return {
    logic: "all",
    conditions: stageId
      ? [{ field: "stage_id", operator: "equals", value: stageId }]
      : [],
    trigger_delay_minutes: 60,
    reminder_interval_minutes: null,
    max_reminder_days: null,
    max_reminder_count: null,
  };
}

function normalizeLeadRuleFormFromApi(rule) {
  let triggerType = rule.trigger_type || "message_received";
  let filters = rule.filters || { logic: "all", conditions: [] };
  if (triggerType === "platform_event") {
    const eventType = (filters.conditions || []).find((c) => c.field === "event_type")?.value;
    if (isLeadFollowupPlatformEvent(eventType)) {
      triggerType = LEAD_STAGE_FOLLOWUP_TRIGGER;
      const stageId = (filters.conditions || []).find((c) => c.field === "stage_id")?.value || "";
      filters = {
        ...filters,
        conditions: stageId
          ? [{ field: "stage_id", operator: "equals", value: stageId }]
          : [],
      };
    }
  }
  return { triggerType, filters };
}

function upsertFilterCondition(conditions, field, value) {
  const next = (conditions || []).filter((c) => c.field !== field);
  next.push({ field, operator: "equals", value });
  return next;
}

const ABANDONED_CART_MIN_FOLLOWUP_INTERVAL_MINUTES = 60;
const ABANDONED_CART_MAX_REMINDER_DAYS = 30;
const ABANDONED_CART_MAX_REMINDER_COUNT = 20;

function isAbandonedCartFollowUpEnabled(filters) {
  const interval = Number(filters?.reminder_interval_minutes);
  return Number.isFinite(interval) && interval > 0;
}

function getAbandonedCartSchedulePatch(prevFilters) {
  return {
    trigger_delay_minutes: prevFilters?.trigger_delay_minutes ?? 60,
    reminder_interval_minutes: prevFilters?.reminder_interval_minutes ?? null,
    max_reminder_days: prevFilters?.max_reminder_days ?? 7,
    max_reminder_count: prevFilters?.max_reminder_count ?? null,
  };
}

const DEFAULT_ABANDONED_SCHEDULE_DRAFT = {
  triggerDelayMinutes: "60",
  intervalDays: "2",
  maxReminderDays: "7",
  maxReminderCount: "",
};

function sanitizeNumericDraftInput(raw) {
  return String(raw ?? "").replace(/\D/g, "");
}

function minutesToIntervalDays(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return "2";
  return String(Math.max(1, Math.round(n / (24 * 60))));
}

function syncAbandonedScheduleDraftFromFilters(filters) {
  const f = filters || {};
  const followUp = isAbandonedCartFollowUpEnabled(f);
  return {
    triggerDelayMinutes: f.trigger_delay_minutes != null && f.trigger_delay_minutes !== ""
      ? String(f.trigger_delay_minutes)
      : "60",
    intervalDays: followUp ? minutesToIntervalDays(f.reminder_interval_minutes) : "2",
    maxReminderDays: f.max_reminder_days != null && f.max_reminder_days !== ""
      ? String(f.max_reminder_days)
      : "7",
    maxReminderCount: f.max_reminder_count != null && f.max_reminder_count !== ""
      ? String(f.max_reminder_count)
      : "",
  };
}

function applyAbandonedScheduleDraftToFilters(filters, draft) {
  const f = { ...(filters || { logic: "all", conditions: [] }) };
  const triggerStr = String(draft?.triggerDelayMinutes ?? "").trim();
  const intervalStr = String(draft?.intervalDays ?? "").trim();
  const maxDaysStr = String(draft?.maxReminderDays ?? "").trim();
  const maxCountStr = String(draft?.maxReminderCount ?? "").trim();
  const wantsFollowUp = isAbandonedCartFollowUpEnabled(f)
    || (intervalStr !== "" && Number(intervalStr) > 0);

  if (triggerStr === "") {
    delete f.trigger_delay_minutes;
  } else {
    const triggerNum = Number(triggerStr);
    if (Number.isFinite(triggerNum)) f.trigger_delay_minutes = triggerNum;
  }

  if (wantsFollowUp) {
    const intervalDaysNum = Number(intervalStr);
    if (Number.isFinite(intervalDaysNum) && intervalDaysNum > 0) {
      f.reminder_interval_minutes = Math.max(
        ABANDONED_CART_MIN_FOLLOWUP_INTERVAL_MINUTES,
        intervalDaysNum * 24 * 60,
      );
    } else if (Number.isFinite(Number(f.reminder_interval_minutes)) && Number(f.reminder_interval_minutes) > 0) {
      // keep existing interval when draft empty but follow-up toggle is on
    } else {
      f.reminder_interval_minutes = 1440;
    }
    if (maxDaysStr === "") {
      f.max_reminder_days = f.max_reminder_days ?? 7;
    } else {
      const maxDaysNum = Number(maxDaysStr);
      if (Number.isFinite(maxDaysNum)) f.max_reminder_days = maxDaysNum;
    }
    if (maxCountStr === "") {
      f.max_reminder_count = null;
    } else {
      const maxCountNum = Number(maxCountStr);
      if (Number.isFinite(maxCountNum) && maxCountNum > 0) f.max_reminder_count = maxCountNum;
    }
  } else {
    f.reminder_interval_minutes = null;
    f.max_reminder_days = null;
    f.max_reminder_count = null;
  }

  return f;
}

function estimatedAbandonedCartReminders(filters) {
  if (!isAbandonedCartFollowUpEnabled(filters)) return 1;
  const maxDays = Number(filters?.max_reminder_days ?? 7);
  const interval = Number(filters?.reminder_interval_minutes ?? 2880);
  const explicitMax = Number(filters?.max_reminder_count);
  if (Number.isFinite(explicitMax) && explicitMax > 0) {
    return Math.min(ABANDONED_CART_MAX_REMINDER_COUNT, explicitMax);
  }
  if (!Number.isFinite(interval) || interval <= 0) return 1;
  return Math.max(
    1,
    Math.min(
      ABANDONED_CART_MAX_REMINDER_COUNT,
      Math.floor((maxDays * 24 * 60) / interval) + 1,
    ),
  );
}

const ESTORE_PLATFORM_EVENTS = [
  { value: "estore/orders/pending", label: "Estore — Pending" },
  { value: "estore/orders/paypending", label: "Estore — Payment pending" },
  { value: "estore/orders/paid", label: "Estore — Paid" },
  { value: "estore/orders/accepted", label: "Estore — Accepted" },
  { value: "estore/orders/shipped", label: "Estore — Shipped" },
  { value: "estore/orders/delivered", label: "Estore — Delivered" },
  { value: "estore/orders/completed", label: "Estore — Completed" },
  { value: "estore/orders/closed", label: "Estore — Closed" },
  { value: "estore/orders/cancelled", label: "Estore — Cancelled" },
  { value: "estore/orders/failed", label: "Estore — Failed" },
  { value: "estore/orders/refunded", label: "Estore — Refunded" },
  { value: "estore/cart/abandoned", label: "Abandoned cart" },
];

const VERTICAL_PLATFORM_EVENTS = [
  { value: "APPOINTMENT_CONFIRMED.v1", label: "Appointment confirmed" },
  { value: "APPOINTMENT_RESCHEDULED.v1", label: "Appointment rescheduled" },
  { value: "APPOINTMENT_CANCELLED.v1", label: "Appointment cancelled" },
  { value: "APPOINTMENT_REMINDER_24H.v1", label: "Appointment reminder 24h" },
  { value: "APPOINTMENT_REMINDER_1H.v1", label: "Appointment reminder 1h" },
];

function platformEventsForPlatform(platform) {
  if (platform === "estore") return ESTORE_PLATFORM_EVENTS;
  if (platform === "whatsapp_vertical") return VERTICAL_PLATFORM_EVENTS;
  return SHOPIFY_PLATFORM_EVENTS;
}

function defaultEventForPlatform(platform) {
  if (platform === "estore") return "estore/orders/accepted";
  if (platform === "whatsapp_vertical") return "APPOINTMENT_CONFIRMED.v1";
  return "orders/create";
}

const APPOINTMENT_SELF_SERVICE_TEMPLATES = [
  {
    id: "appointment_cancel",
    label: "Appointment cancel instructions",
    text:
      "To cancel your appointment, reply CANCEL {{booking_id}}. We will confirm once it is cancelled.",
  },
  {
    id: "appointment_reschedule",
    label: "Appointment reschedule instructions",
    text:
      "To reschedule your appointment, reply RESCHEDULE {{booking_id}} and share your preferred date/time.",
  },
  {
    id: "appointment_manage",
    label: "Appointment manage options",
    text:
      "Manage appointment #{{booking_id}}:\n1. Reply RESCHEDULE to choose another slot\n2. Reply CANCEL to cancel this booking",
  },
];

function extractTemplateVariables(body) {
  const vars = [];
  const seen = new Set();
  const re = /\{\{(\d+|[a-zA-Z_][a-zA-Z0-9_]*)\}\}/g;
  let m;
  while ((m = re.exec(String(body || ""))) !== null) {
    if (!seen.has(m[1])) { seen.add(m[1]); vars.push(m[1]); }
  }
  return vars;
}

/** Rule keyword condition `value` is stored comma-separated for OR matching. */
function parseRuleKeywordList(value) {
  return String(value ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function joinRuleKeywordList(parts) {
  return parts.map((s) => String(s).trim()).filter(Boolean).join(", ");
}

const AutomationView = ({ onNavigateToFlow }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [activeTab, setActiveTab] = useState("rules");

  // Add Keyword Action Dialog State
  const [isAddKeywordDialogOpen, setIsAddKeywordDialogOpen] = useState(false);
  const [isFallbackDialogOpen, setIsFallbackDialogOpen] = useState(false);
  const [keywordAction, setKeywordAction] = useState({
    id: null,
    keyword: "",
    chatbot: "",
    chatbotId: null,
    matchingMethod: "Exact",
  });
  const [keywordActionErrors, setKeywordActionErrors] = useState({});
  const [automations, setAutomation] = useState([]);
  const [chatbots, setChatbots] = useState([])
  const [isCreateChatbotDialogOpen, setIsCreateChatbotDialogOpen] =
    useState(false);
  const [newChatbot, setNewChatbot] = useState({
    name: "",
  });
  const [chatbotErrors, setChatbotErrors] = useState({});
  const [keywordActions, setKeywordActions] = useState([]);
  const [rules, setRules] = useState([]);
  const [ruleLogs, setRuleLogs] = useState([]);
  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [isRuleDeleteDialogOpen, setIsRuleDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [isFormDeleteDialogOpen, setIsFormDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [ruleErrors, setRuleErrors] = useState({});
  const [ruleKeywordDraft, setRuleKeywordDraft] = useState("");
  const [abandonedScheduleDraft, setAbandonedScheduleDraft] = useState(DEFAULT_ABANDONED_SCHEDULE_DRAFT);
  const [ruleForm, setRuleForm] = useState({
    rule_name: "",
    trigger_type: "message_received",
    enabled: true,
    priority: 100,
    stop_on_match: true,
    filters: { logic: "all", conditions: [] },
    actions: [{ type: "send_chatbot", chatbot_id: "" }],
  });
  const [routingStaff, setRoutingStaff] = useState([]);
  const [tags, setTags] = useState([]);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [aiPersonas, setAiPersonas] = useState([]);
  const [noKeywordFallbackConfig, setNoKeywordFallbackConfig] = useState({
    enabled: false,
    fallbackChatbotId: null,
  });
  const [forms, setForms] = useState([]);
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [libraryTemplateCount, setLibraryTemplateCount] = useState(0);
  const [yourBotsSearch, setYourBotsSearch] = useState("");

  const chatbotsListTab = searchParams.get("view") === "library" ? "library" : "yours";
  const setChatbotsListTab = (tab) => {
    if (tab === "library") {
      setSearchParams({ view: "library" });
    } else {
      setSearchParams({});
    }
  };

  const filteredYourChatbots = useMemo(() => {
    const q = yourBotsSearch.trim().toLowerCase();
    if (!q) return automations;
    return automations.filter((a) => {
      const name = (a.getTemplateName() || "").toLowerCase();
      const desc = (a.getDescription() || "").toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [automations, yourBotsSearch]);

  function fetch() {
    AutomationService.getChatBot().then((r) => {
      console.log('r', r);
      
      setAutomation(r);
      const formatted = r.map((res) => ({
        id: res.getId(),
        name: res.getTemplateName(),
        status: res.getStatus(),
        trigger: res.getTrigger(),
        description: res.getDescription(),
        action: res.getAction(),
        contacts: res.getContacts() || [],
        lastRun: res.getLastRun(),
        nextRun: res.getNextRun(),
        sourceDefaultChatbotId: res.getSourceDefaultChatbotId ? res.getSourceDefaultChatbotId() : null,
      }));
  
      setChatbots(formatted);
    });
    AutomationService.getKeyWords().then((res) => {
      const formatRows = res.map((res) => ({
        id: res.getId(),
        keyword: res.getKeyword(),
        triggered: res.getTriggerNo(),
        matchingMethod: res.getMatchMethod(),
        replyMaterial: res.getReplyMaterial(),
        chatbot: res.getChatbotName(),
        chatbotId: res.getChatbotId(),
        templateId: res.getTemplateId(),
      }));
      setKeywordActions(formatRows);
    });
    AutomationService.getAutomationRules()
      .then((rows) => setRules(Array.isArray(rows) ? rows : []))
      .catch(() => setRules([]));
    AutomationService.getAutomationRuleLogs(50)
      .then((rows) => setRuleLogs(Array.isArray(rows) ? rows : []))
      .catch(() => setRuleLogs([]));
    AutomationService.getNoKeywordFallbackConfig()
      .then((cfg) => {
        setNoKeywordFallbackConfig({
          enabled: !!cfg?.enabled,
          fallbackChatbotId: cfg?.fallback_chatbot_id ?? cfg?.fallbackChatbotId ?? null,
        });
      })
      .catch(() => {
        setNoKeywordFallbackConfig({
          enabled: false,
          fallbackChatbotId: null,
        });
      });

    const serviceId = appConfig.getServiceId();
    if (serviceId) {
      AutomationService.getForms(serviceId).then((data) => setForms(data || [])).catch(() => setForms([]));
    }
    AutomationService.getDefaultChatbots()
      .then((d) => setLibraryTemplateCount(Array.isArray(d) ? d.length : 0))
      .catch(() => setLibraryTemplateCount(0));
    ConversationService.getRoutingStaff()
      .then((rows) => setRoutingStaff(Array.isArray(rows) ? rows : []))
      .catch(() => setRoutingStaff([]));
    TagService.getAllTags({ limit: 500 })
      .then((rows) => setTags(Array.isArray(rows) ? rows : []))
      .catch(() => setTags([]));
    MessageTemplateService.getAllTemplates()
      .then((rows) => {
        const approved = (rows || []).filter((t) => {
          const status = String(t?.attrs?.status || "").toLowerCase();
          return status !== "rejected";
        });
        setMessageTemplates(approved);
      })
      .catch(() => setMessageTemplates([]));
    servicesService.getAiPersonas()
      .then((rows) => setAiPersonas(Array.isArray(rows) ? rows : []))
      .catch(() => setAiPersonas([]));
  }

  useEffect(() => {
    fetch();
  }, [])

  useEffect(() => {
    const tab = location.pathname.split("/")[2];
    const validTabs = ["rules", "keyword-actions", "chatbots", "forms"];
    if (validTabs.includes(tab)) {
      setActiveTab(tab);
    } else {
      setActiveTab("rules");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isRuleDialogOpen) return;
    LeadService.getPipelineStages()
      .then((stages) => setPipelineStages(Array.isArray(stages) ? stages : []))
      .catch(() => setPipelineStages([]));
  }, [isRuleDialogOpen]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/automation/${tab}`);
  };

  const openCreateRuleDialog = () => {
    setEditingRuleId(null);
    setRuleErrors({});
    setRuleKeywordDraft("");
    setAbandonedScheduleDraft({ ...DEFAULT_ABANDONED_SCHEDULE_DRAFT });
    setRuleForm({
      rule_name: "",
      trigger_type: "message_received",
      enabled: true,
      priority: 100,
      stop_on_match: true,
      filters: { logic: "all", conditions: [{ field: "keyword", operator: "contains", value: "", match_method: "CONTAINS" }] },
      actions: [{ type: "send_chatbot", chatbot_id: "" }],
    });
    setIsRuleDialogOpen(true);
  };

  const openEditRuleDialog = (rule) => {
    setEditingRuleId(rule.id);
    setRuleErrors({});
    setRuleKeywordDraft("");
    const normalized = normalizeLeadRuleFormFromApi(rule);
    const filters = isInboundPayloadTriggerWithoutKeywords(normalized.triggerType)
      ? { logic: "all", conditions: [] }
      : normalized.filters;
    setAbandonedScheduleDraft(syncAbandonedScheduleDraftFromFilters(filters));
    setRuleForm({
      rule_name: rule.rule_name || "",
      trigger_type: normalized.triggerType,
      enabled: !!rule.enabled,
      priority: Number(rule.priority ?? 100),
      stop_on_match: !!rule.stop_on_match,
      filters,
      actions:
        Array.isArray(rule.actions) && rule.actions.length > 0
          ? rule.actions
          : isLeadStageFollowupTrigger(normalized.triggerType)
            ? [{ type: "send_template", template_id: "", variable_paths: { header: {}, body: {} } }]
            : isPhoneCallAutomationTrigger(normalized.triggerType)
              ? [{ type: "send_template", template_id: "", variable_paths: { header: {}, body: {} } }]
            : [{ type: "send_chatbot", chatbot_id: "" }],
    });
    setIsRuleDialogOpen(true);
  };

  const buildRuleFormForSave = () => {
    const eventType = (ruleForm.filters?.conditions || []).find((c) => c.field === "event_type")?.value;
    if (ruleForm.trigger_type === "platform_event" && isStageFollowUpScheduleEvent(eventType)) {
      return {
        ...ruleForm,
        filters: applyAbandonedScheduleDraftToFilters(ruleForm.filters, abandonedScheduleDraft),
      };
    }
    if (isLeadStageFollowupTrigger(ruleForm.trigger_type)) {
      return {
        ...ruleForm,
        filters: applyAbandonedScheduleDraftToFilters(ruleForm.filters, abandonedScheduleDraft),
      };
    }
    if (isInboundPayloadTriggerWithoutKeywords(ruleForm.trigger_type)) {
      return {
        ...ruleForm,
        filters: { logic: "all", conditions: [] },
      };
    }
    return ruleForm;
  };

  const validateRuleForm = (formOverride) => {
    const form = formOverride || ruleForm;
    const errors = {};
    if (!form.rule_name?.trim()) {
      errors.rule_name = "Rule name is required";
    }
    if (!Array.isArray(form.actions) || form.actions.length === 0) {
      errors.actions = "At least one action is required";
    } else {
      const first = form.actions[0];
      if (first.type === "send_chatbot" && !first.chatbot_id) {
        errors.actions = "Select a chatbot action target";
      }
      if (first.type === "send_text" && !String(first.text || "").trim()) {
        errors.actions = "Message text is required for send text action";
      }
      if (first.type === "assign_to_account" && !String(first.account_id || "").trim()) {
        errors.actions = "Account ID is required for assignment action";
      }
      if (first.type === "add_tag" && !String(first.tag_id || "").trim()) {
        errors.actions = "Tag ID is required for add tag action";
      }
      if (first.type === "send_template" && !String(first.template_id || "").trim()) {
        errors.actions = "Template is required for platform template action";
      }
      if (first.type === "ai_autoreply") {
        if (!String(first.ai_provider_key || first.provider_key || "").trim()) {
          errors.actions = "Select AI provider for AI auto-reply action";
        }
        if (!String(first.ai_persona_preset_id || "").trim()) {
          errors.actions = "Persona preset id is required for AI auto-reply action";
        }
        const h = Number(first.ai_session_hours);
        if (!Number.isFinite(h) || h <= 0) {
          errors.actions = "AI reply duration (hours) must be greater than 0";
        }
      }
      if (first.type === "visual_product_search") {
        if (!String(first.fallback_text || "").trim()) {
          errors.actions = "Fallback message is required for visual product search";
        }
      }
    }
    if (form.trigger_type === "platform_event") {
      const conditions = form?.filters?.conditions || [];
      const hasPlatform = conditions.some((c) => c.field === "platform" && String(c.value || "").trim());
      const hasEvent = conditions.some((c) => c.field === "event_type" && String(c.value || "").trim());
      if (!hasPlatform || !hasEvent) {
        errors.filters = "Platform and event filters are required for platform_event trigger";
      }
      const eventType = conditions.find((c) => c.field === "event_type")?.value;
      if (isAbandonedCartPlatformEvent(eventType)) {
        const delay = Number(form.filters?.trigger_delay_minutes);
        if (!Number.isFinite(delay) || delay < 5) {
          errors.filters = "First reminder must be at least 5 minutes after last cart activity";
        } else if (isAbandonedCartFollowUpEnabled(form.filters)) {
          const interval = Number(form.filters?.reminder_interval_minutes);
          if (!Number.isFinite(interval) || interval < ABANDONED_CART_MIN_FOLLOWUP_INTERVAL_MINUTES) {
            errors.filters = `Follow-up interval must be at least ${ABANDONED_CART_MIN_FOLLOWUP_INTERVAL_MINUTES} minutes`;
          }
          const maxDays = Number(form.filters?.max_reminder_days);
          if (!Number.isFinite(maxDays) || maxDays < 1 || maxDays > ABANDONED_CART_MAX_REMINDER_DAYS) {
            errors.filters = `Stop-after must be between 1 and ${ABANDONED_CART_MAX_REMINDER_DAYS} days`;
          }
          const maxCountRaw = form.filters?.max_reminder_count;
          if (maxCountRaw != null && maxCountRaw !== "") {
            const maxCount = Number(maxCountRaw);
            if (!Number.isFinite(maxCount) || maxCount < 1 || maxCount > ABANDONED_CART_MAX_REMINDER_COUNT) {
              errors.filters = `Max reminders must be between 1 and ${ABANDONED_CART_MAX_REMINDER_COUNT}`;
            }
          }
        }
      } else if (isLeadFollowupPlatformEvent(eventType)) {
        const stageId = conditions.find((c) => c.field === "stage_id")?.value;
        if (!String(stageId || "").trim()) {
          errors.filters = "Pipeline stage is required for lead follow-up rules";
        }
        const delay = Number(form.filters?.trigger_delay_minutes);
        if (!Number.isFinite(delay) || delay < 5) {
          errors.filters = "First reminder must be at least 5 minutes after entering the stage";
        } else if (isAbandonedCartFollowUpEnabled(form.filters)) {
          const interval = Number(form.filters?.reminder_interval_minutes);
          if (!Number.isFinite(interval) || interval < ABANDONED_CART_MIN_FOLLOWUP_INTERVAL_MINUTES) {
            errors.filters = `Follow-up interval must be at least ${ABANDONED_CART_MIN_FOLLOWUP_INTERVAL_MINUTES} minutes`;
          }
          const maxDays = Number(form.filters?.max_reminder_days);
          if (!Number.isFinite(maxDays) || maxDays < 1 || maxDays > ABANDONED_CART_MAX_REMINDER_DAYS) {
            errors.filters = `Stop-after must be between 1 and ${ABANDONED_CART_MAX_REMINDER_DAYS} days`;
          }
          const maxCountRaw = form.filters?.max_reminder_count;
          if (maxCountRaw != null && maxCountRaw !== "") {
            const maxCount = Number(maxCountRaw);
            if (!Number.isFinite(maxCount) || maxCount < 1 || maxCount > ABANDONED_CART_MAX_REMINDER_COUNT) {
              errors.filters = `Max reminders must be between 1 and ${ABANDONED_CART_MAX_REMINDER_COUNT}`;
            }
          }
        }
      }
    }
    if (isLeadStageFollowupTrigger(form.trigger_type)) {
      const conditions = form?.filters?.conditions || [];
      const stageId = conditions.find((c) => c.field === "stage_id")?.value;
      if (!String(stageId || "").trim()) {
        errors.filters = "Pipeline stage is required";
      }
      const delay = Number(form.filters?.trigger_delay_minutes);
      if (!Number.isFinite(delay) || delay < 5) {
        errors.filters = "First template must be at least 5 minutes after entering the stage";
      } else if (isAbandonedCartFollowUpEnabled(form.filters)) {
        const interval = Number(form.filters?.reminder_interval_minutes);
        if (!Number.isFinite(interval) || interval < ABANDONED_CART_MIN_FOLLOWUP_INTERVAL_MINUTES) {
          errors.filters = `Follow-up interval must be at least ${ABANDONED_CART_MIN_FOLLOWUP_INTERVAL_MINUTES} minutes`;
        }
        const maxDays = Number(form.filters?.max_reminder_days);
        if (!Number.isFinite(maxDays) || maxDays < 1 || maxDays > ABANDONED_CART_MAX_REMINDER_DAYS) {
          errors.filters = `Stop-after must be between 1 and ${ABANDONED_CART_MAX_REMINDER_DAYS} days`;
        }
        const maxCountRaw = form.filters?.max_reminder_count;
        if (maxCountRaw != null && maxCountRaw !== "") {
          const maxCount = Number(maxCountRaw);
          if (!Number.isFinite(maxCount) || maxCount < 1 || maxCount > ABANDONED_CART_MAX_REMINDER_COUNT) {
            errors.filters = `Max reminders must be between 1 and ${ABANDONED_CART_MAX_REMINDER_COUNT}`;
          }
        }
      }
      const first = form.actions?.[0];
      if (!String(first?.template_id || "").trim()) {
        errors.actions = "Template is required for lead stage follow-up";
      }
    }
    if (isPhoneCallAutomationTrigger(form.trigger_type)) {
      const first = form.actions?.[0];
      if (!String(first?.template_id || "").trim()) {
        errors.actions = "Template is required for phone call automation";
      }
    }
    const keywordCondition = (form?.filters?.conditions || []).find((c) => c.field === "keyword");
    if (keywordCondition && !isInboundPayloadTriggerWithoutKeywords(form.trigger_type)) {
      if (parseRuleKeywordList(keywordCondition.value).length === 0) {
        errors.filters = "Add at least one keyword";
      }
    }
    setRuleErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const commitRuleKeywordDraft = () => {
    const additions = parseRuleKeywordList(ruleKeywordDraft);
    if (additions.length === 0) return;
    setRuleForm((prev) => {
      const mm =
        (prev.filters?.conditions || []).find((c) => c.field === "keyword")?.match_method || "CONTAINS";
      const current = parseRuleKeywordList(
        (prev.filters?.conditions || []).find((c) => c.field === "keyword")?.value || "",
      );
      const seen = new Set(current.map((k) => k.toLowerCase()));
      const merged = [...current];
      additions.forEach((a) => {
        const low = a.toLowerCase();
        if (!seen.has(low)) {
          seen.add(low);
          merged.push(a);
        }
      });
      return {
        ...prev,
        filters: {
          logic: "all",
          conditions: [
            {
              field: "keyword",
              operator: "contains",
              value: joinRuleKeywordList(merged),
              match_method: mm,
            },
          ],
        },
      };
    });
    setRuleKeywordDraft("");
  };

  const handleSaveRule = async () => {
    const formToSave = buildRuleFormForSave();
    if (!validateRuleForm(formToSave)) return;
    try {
      if (editingRuleId) {
        await AutomationService.updateAutomationRule(editingRuleId, formToSave);
      } else {
        await AutomationService.createAutomationRule(formToSave);
      }
      setSnackbar({
        open: true,
        message: editingRuleId ? "Rule updated successfully!" : "Rule created successfully!",
        severity: "success",
      });
      setIsRuleDialogOpen(false);
      fetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to save rule",
        severity: "error",
      });
    }
  };

  const openDeleteRuleDialog = (rule) => {
    setRuleToDelete(rule || null);
    setIsRuleDeleteDialogOpen(true);
  };

  const handleDeleteRule = async () => {
    if (!ruleToDelete?.id) return;
    try {
      await AutomationService.deleteAutomationRule(ruleToDelete.id);
      setSnackbar({ open: true, message: "Rule deleted.", severity: "success" });
      setIsRuleDeleteDialogOpen(false);
      setRuleToDelete(null);
      fetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Rule could not be deleted.",
        severity: "error",
      });
    }
  };

  const handleSaveNoKeywordFallbackConfig = async () => {
    try {
      await AutomationService.saveNoKeywordFallbackConfig({
        enabled: noKeywordFallbackConfig.enabled,
        fallback_chatbot_id: noKeywordFallbackConfig.fallbackChatbotId,
      });
      setSnackbar({
        open: true,
        message: 'No-keyword fallback saved!',
        severity: 'success',
      });
      setIsFallbackDialogOpen(false);
      fetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || 'Failed to save fallback config',
        severity: 'error',
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "paused":
        return "warning";
      case "draft":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <PlayIcon fontSize="small" />;
      case "paused":
        return <PauseIcon fontSize="small" />;
      default:
        return <SettingsIcon fontSize="small" />;
    }
  };

  // const handleCreateAutomation = () => {
  //   console.log("Create Automation button clicked");
  //   if (onNavigateToFlow) {
  //     console.log("Navigating to Flow screen");
  //     onNavigateToFlow();
  //   } else {
  //     console.log("Opening create dialog (fallback)");
  //     setIsCreateDialogOpen(true);
  //   }
  // };
  const handleEditAutomation = (automation) => {
    if (onNavigateToFlow) {
      onNavigateToFlow(automation.getId());
    } else {
      setSelectedAutomation(automation);
      setIsCreateDialogOpen(true);
    }
  };

  async function handleDeleteAutomation(automation) {
    try {
      await AutomationService.deleteChatbot(automation.getId());
      setSnackbar({
        open: true,
        message: 'Chatbot deleted successfully!',
        severity: "success",
      });
      fetch(false);
    } catch (error) {
      console.log('Chatbot Delete Error:', error);
      setSnackbar({
        open: true,
        message: 'Chatbot not deleted!',
        severity: "error",
      });
    }
    // Handle delete logic
    console.log("Delete automation:", automation.getId());
  };

  const handleToggleStatus = (automation) => {
    console.log("Toggle status for:", automation.id);
  };

  const handleOpenAddKeywordDialog = (row) => {
    setIsAddKeywordDialogOpen(true);
    if (row) {
      setKeywordAction({
        id: row.id,
        keyword: row.keyword,
        chatbot: row.chatbot,
        chatbotId: row.chatbotId,
        matchingMethod: row.matchingMethod || "Exact",
        triggered: row.triggered, // Preserve triggered count
        templateId: row.templateId, // Preserve template ID
      });
    } else {
      setKeywordAction({ keyword: "", chatbot: "", chatbotId: null, id: null, matchingMethod: "Exact", triggered: 0, templateId: null, });
    }
    setKeywordActionErrors({});
  };

  const handleCloseAddKeywordDialog = () => {
    setIsAddKeywordDialogOpen(false);
    setKeywordAction({ keyword: "", chatbot: "", chatbotId: null, id: null, matchingMethod: "Exact" });
    setKeywordActionErrors({});
  };

  const handleKeywordActionChange = (field, value, id) => {
    setKeywordAction((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (keywordActionErrors[field]) {
      setKeywordActionErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateKeywordAction = () => {
    const errors = {};

    if (!keywordAction.keyword.trim()) {
      errors.keyword = "Keyword is required";
    }

    if (!keywordAction.chatbot) {
      errors.chatbot = "Please select a chatbot";
    }

    setKeywordActionErrors(errors);
    return Object.keys(errors).length === 0;
  };

  async function handleSaveKeywordAction() {
    if (validateKeywordAction()) {
      const isEdit = !!keywordAction.id;

      // Check for duplicate keyword (same keyword and same chatbot)
      const duplicateExists = keywordActions.some(
        (item) => {
          // If editing, exclude the current item from duplicate check
          if (isEdit && item.id === keywordAction.id) {
            return false;
          }
          // Check if same keyword (case-insensitive) and same chatbot
          return item.keyword.toLowerCase() === keywordAction.keyword.trim().toLowerCase() &&
            item.chatbotId === keywordAction.chatbotId;
        }
      );
      if (duplicateExists) {
        setSnackbar({
          open: true,
          message: 'Duplicate keyword! This keyword already exists for this chatbot.',
          severity: "error",
        });
        return;
      }
      let newKeywordAction;
      if (isEdit) {
        newKeywordAction = {
          id: keywordAction.id,
          chatbotId: keywordAction.chatbotId,
          templateId: keywordAction.templateId || null,
          keyword: keywordAction.keyword.trim(),
          triggered: keywordAction.triggered || 0,
          matchingMethod: keywordAction.matchingMethod,
          replyMaterial: "Auto-reply",
          chatbot: keywordAction.chatbot,
        };
        setKeywordActions((prev) => prev.map((item) =>
          item.id === newKeywordAction.id ? newKeywordAction : item
        ));
      } else {
        newKeywordAction = {
          id: null,
          chatbotId: keywordAction.chatbotId,
          templateId: keywordAction.templateId || null,
          keyword: keywordAction.keyword.trim(),
          triggered: 0,
          matchingMethod: keywordAction.matchingMethod,
          replyMaterial: "Auto-reply",
          chatbot: keywordAction.chatbot,
        };
        setKeywordActions((prev) => [...prev, newKeywordAction]);
      }

      try {
        await AutomationService.saveKeyWordAction(newKeywordAction);
        setSnackbar({
          open: true,
          message: 'Keyword saved successfully!',
          severity: "success",
        });
      } catch (error) {
        console.log('Keyword Save Error:', error);
        setSnackbar({
          open: true,
          message: error.response?.data?.message || 'Keyword not saved! or Duplicate keyword',
          severity: "error",
        });
      }
      handleCloseAddKeywordDialog();
      fetch();
      // Show success message (you can add a snackbar here)
      console.log("Keyword action saved:", newKeywordAction);
    }
  }

  // Delete keyword action
  async function handleDeleteKeywordAction(row) {
    try {
      await AutomationService.deleteKeyWord(row.id);
      setKeywordActions((prev) => prev.filter((item) => item.id !== row.id));
      setSnackbar({
        open: true,
        message: 'Keyword deleted successfully!',
        severity: "success",
      });
      fetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Keyword not deleted!',
        severity: "error",
      });
    }
  }

  function snackBar() {
    return (
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    )
  }

  // Create Chatbot Dialog Handlers
  const handleOpenCreateChatbotDialog = () => {
    setIsCreateChatbotDialogOpen(true);
    setNewChatbot({ name: "" });
    setChatbotErrors({});
  };

  const handleCloseCreateChatbotDialog = () => {
    setIsCreateChatbotDialogOpen(false);
    setNewChatbot({ name: "" });
    setChatbotErrors({});
  };

  const handleChatbotNameChange = (value) => {
    setNewChatbot((prev) => ({
      ...prev,
      name: value,
    }));

    // Clear error when user starts typing
    if (chatbotErrors.name) {
      setChatbotErrors((prev) => ({
        ...prev,
        name: "",
      }));
    }
  };

  const validateChatbot = () => {
    const errors = {};

    if (!newChatbot.name.trim()) {
      errors.name = "Chatbot name is required";
    }

    // Check for duplicate names
    const isDuplicate = chatbots.some(
      (chatbot) =>
        chatbot.name.toLowerCase() === newChatbot.name.trim().toLowerCase()
    );

    if (isDuplicate) {
      errors.name = "A chatbot with this name already exists";
    }

    setChatbotErrors(errors);
    return Object.keys(errors).length === 0;
  };

    async function handleCreateChatbot() {
    if (validateChatbot()) {
      const newChatbotData = {
        id: null,
        name: newChatbot.name.trim(),
        description: "3-day follow-up sequence for leads",
        trigger: "Tag Added",
        action: "Message Sequence",
        status: "active",

      };
      // const newChat = {
      //   id: null,
      //   name: newChatbot.name.trim(),
      // }
      setNewChatbot((prev) => ({
        ...prev, newChatbotData }));
      try {
        await AutomationService.createChatBot(newChatbotData);
        setSnackbar({
          open: true,
          message: 'Chatbot created successfully!',
          severity: "success",
        });
        fetch(false);
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Chatbot not saved!',
          severity: "error",
        });
        console.log('Chatbot Error:', error);
        
      }
      handleCloseCreateChatbotDialog();
      fetch();
      // Show success message (you can add a snackbar here)
      console.log("Chatbot created:", newChatbotData);
    }
  };

  // Form dialog handlers
  const handleOpenFormDialog = (form = null) => {
    setSelectedFormId(form?.id || null);
    setIsFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setIsFormDialogOpen(false);
    setSelectedFormId(null);
  };

  const openDeleteFormDialog = (form) => {
    setFormToDelete(form || null);
    setIsFormDeleteDialogOpen(true);
  };

  const handleDeleteForm = async () => {
    if (!formToDelete?.id) return;
    try {
      await AutomationService.deleteForm(formToDelete.id);
      setSnackbar({ open: true, message: "Form deleted.", severity: "success" });
      setIsFormDeleteDialogOpen(false);
      setFormToDelete(null);
      fetch();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || "Form could not be deleted.",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex" }}>
      {/* Left Sidebar */}
      {/* <Box
        sx={{
        width: { xs: "100%", md: "20%" },
          minWidth: { xs: 0, md: 240 },
          background: "#ffffff",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
          mx: 1,
                  mb: 0.5,
          }}
        >
         <Box sx={{ p: 3,
                 //  borderBottom: "1px solid #e0e0e0"
                   }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, color: "#333", fontFamily: 'Poppins, sans-serif' }}>
                   Automation
                 </Typography>
                 <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontFamily: 'Poppins, sans-serif' }}>
                   Manage keyword actions and chatbots
                 </Typography>
               </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Box
  onClick={() => handleTabChange("keyword-actions")}
  sx={{
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    px: 2,
    py: 1.5,
    borderRadius: 1,
    cursor: "pointer",
    position: "relative",
    bgcolor: activeTab === "keyword-actions" ? "#eef3ff" : "#fff",
    color: activeTab === "keyword-actions" ? "#4f7cff" : "#6b7280",
    borderRight:
    activeTab === "keyword-actions"
    ? "4px solid #4f7cff"
    : "4px solid transparent",
    "&:hover": {
      bgcolor: "#f5f7fb",
    },
  }}
>
  <KeyOutlinedIcon fontSize="small" />
  <Typography
    sx={{
      fontSize: 14,
      fontWeight: 500,
      fontFamily: "'Poppins', sans-serif",
    }}
  >
    Keyword Actions
  </Typography>
</Box>
          <Box
  onClick={() => handleTabChange("chatbots")}
  sx={{
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    py: 1.5,
    px: 2,
    borderRadius: 1,
    cursor: "pointer",
    position: "relative",
    bgcolor: activeTab === "chatbots" ? "#eef3ff" : "#fff",
    borderRight:
     activeTab === "chatbots"
    ? "4px solid #4f7cff"
    : "4px solid transparent",
    color: activeTab === "chatbots" ? "#4f7cff" : "#6b7280",
    "&:hover": {
      bgcolor: "#f5f7fb",
    },
  }}
>
  <SmartToyOutlinedIcon fontSize="small" />
  <Typography
    sx={{
      fontSize: 14,
      fontWeight: 500,
      fontFamily: "'Poppins', sans-serif",
    }}
  >
    Chatbots
  </Typography>
</Box>
            <Box
              onClick={() => handleTabChange("forms")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1.5,
                px: 2,
                borderRadius: 1,
                cursor: "pointer",
                position: "relative",
                bgcolor: activeTab === "forms" ? "#eef3ff" : "#fff",
                borderRight:
                  activeTab === "forms"
                    ? "4px solid #4f7cff"
                    : "4px solid transparent",
                color: activeTab === "forms" ? "#4f7cff" : "#6b7280",
                "&:hover": {
                  bgcolor: "#f5f7fb",
                },
              }}
            >
              <DescriptionIcon fontSize="small" />
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                Forms
              </Typography>
            </Box>

          </Box>
        </Box>
      </Box> */}

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          p: activeTab === "keyword-actions" ? 2 : 3,
        }}
      >
        {/* Header */}
        {activeTab === "rules" || activeTab === "chatbots" || activeTab === "forms" ? (
        <Box
          sx={{
             display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: activeTab === "keyword-actions" ? 2 : 3,
                  pb: 2,
                  gap: 3,
                   borderBottom:
              activeTab !== "keyword-actions" ? "1px solid #e0e0e0" : "none",
          }}
        >
          {activeTab === "rules" && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "#1976d2", width: 40, height: 40 }}>
                  <AutomationIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "#333", fontFamily: 'Poppins, sans-serif' }}>
                    Rules
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Build automation using When - Filter - Then.
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreateRuleDialog}
                sx={{
                  backgroundColor: "#1976d2",
                  "&:hover": { backgroundColor: "#1565c0" },
                  borderRadius: 2,
                  px: 3,
                }}
              >
                Create Rule
              </Button>
            </>
          )}
          {activeTab === "chatbots" && (
            <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: "#1976d2", width: 40, height: 40 }}>
              <SmartToyOutlinedIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: "#333", fontFamily: 'Poppins, sans-serif' }}>
                Chatbots
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                Select a chatbot below and make it your own by customising it.
              </Typography>
            </Box>
          </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Tooltip title="Create a new chatbot">
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreateChatbotDialog}
                    sx={{
                      backgroundColor: "#1976d2",
                      "&:hover": { backgroundColor: "#1565c0" },
                      borderRadius: 2,
                      px: 3,
                    }}
                  >
                    Create Chatbot
                  </Button>
                </Tooltip>
              </Box>
            </>
          )}
          {activeTab === "forms" && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "#1976d2", width: 40, height: 40 }}>
                  <DescriptionIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: "#333", fontFamily: 'Poppins, sans-serif' }}>
                    Forms
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Configure forms in Meta WhatsApp Business Manager (Forms section).
                  </Typography>
                </Box>
              </Box>
              <Tooltip title="Add or edit a form">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenFormDialog()}
                  sx={{
                    backgroundColor: "#1976d2",
                    "&:hover": { backgroundColor: "#1565c0" },
                    borderRadius: 2,
                    px: 3,
                  }}
                >
                  Configure Form
                </Button>
              </Tooltip>
            </>
          )}
        </Box>
        ) : null}

        {activeTab === "chatbots" && (
          <Box sx={{ mb: 2, width: "100%", px: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { sm: "center" },
                justifyContent: "space-between",
                gap: 2,
                borderBottom: "1px solid #e0e0e0",
                pb: 0,
              }}
            >
              <Tabs
                value={chatbotsListTab}
                onChange={(_, v) => setChatbotsListTab(v)}
                sx={{
                  minHeight: 48,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontFamily: "'Poppins', sans-serif",
                  },
                }}
                textColor="primary"
                TabIndicatorProps={{ sx: { height: 3, borderRadius: "3px 3px 0 0" } }}
              >
                <Tab
                  value="yours"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                      Your bots
                      <Chip
                        size="small"
                        label={automations.length}
                        sx={{ height: 22, minWidth: 24, fontWeight: 600 }}
                      />
                    </Box>
                  }
                />
                <Tab
                  value="library"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                      Library
                      <Chip
                        size="small"
                        color="default"
                        label={libraryTemplateCount}
                        sx={{ height: 22, minWidth: 24, fontWeight: 600 }}
                      />
                    </Box>
                  }
                />
              </Tabs>
              {chatbotsListTab === "yours" ? (
                <TextField
                  size="small"
                  placeholder="Search your bots…"
                  value={yourBotsSearch}
                  onChange={(e) => setYourBotsSearch(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon fontSize="small" sx={{ color: "#9ca3af" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: { sm: 260 },
                    maxWidth: 400,
                    alignSelf: { xs: "stretch", sm: "center" },
                    "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#fff" },
                  }}
                />
              ) : null}
            </Box>
          </Box>
        )}

        {/* ===== EMPTY CHATBOTS STATE ===== */}
        {activeTab === "chatbots" && chatbotsListTab === "yours" && automations.length === 0 && (
          <Box
            sx={{
              flex: 1,
              minHeight: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              textAlign: "center",
              color: "#9ca3af",
            }}
          >
            <SmartToyOutlinedIcon sx={{ fontSize: 64, mb: 1.5 }} />
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: "#374151",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              No Chatbots Yet
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                maxWidth: 360,
                mb: 2,
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              Create a chatbot to automate replies and workflows
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateChatbotDialog}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Create Chatbot
            </Button>
          </Box>
        )}

        {/* ===== EMPTY FORMS STATE ===== */}
        {activeTab === "forms" && forms.length === 0 && (
          <Box
            sx={{
              flex: 1,
              minHeight: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              textAlign: "center",
              color: "#9ca3af",
            }}
          >
            <DescriptionIcon sx={{ fontSize: 64, mb: 1.5 }} />
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: "#374151",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              No Forms Yet
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                maxWidth: 360,
                mb: 2,
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              Configure forms in Meta WhatsApp Business Manager (Forms section), then add them here.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenFormDialog()}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Configure Form
            </Button>
          </Box>
        )}


        {/* Stats Cards - Only show when not in keyword-actions, chatbots or forms tabs */}
        {activeTab !== "keyword-actions" && activeTab !== "chatbots" && activeTab !== "forms" && automations.length > 0 &&(
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "#e3f2fd",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AutomationIcon sx={{ color: "#1976d2" }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {automations.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Workflows
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "#e8f5e8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PlayIcon sx={{ color: "#4caf50" }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {
                          automations.filter((a) => a.getStatus() === "active")
                            .length
                        }
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active Workflows
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "#fff3e0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PauseIcon sx={{ color: "#ff9800" }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {
                          automations.filter((a) => a.getStatus() === "paused")
                            .length
                        }
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Paused Workflows
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "#f3e5f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PersonIcon sx={{ color: "#9c27b0" }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {automations.reduce(
                          (total, a) => total + a.attrs.contacts,
                          0
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Contacts
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Conditional Content Based on Active Tab */}
        {activeTab === "rules" && (
          <Box sx={{ mb: 3 }}>
            {rules.length === 0 ? (
              <Box
                sx={{
                  minHeight: 320,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  textAlign: "center",
                  color: "#9ca3af",
                }}
              >
                <AutomationIcon sx={{ fontSize: 64, mb: 1.5 }} />
                <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#374151", fontFamily: "'Poppins', sans-serif" }}>
                  No Rules Yet
                </Typography>
                <Typography sx={{ fontSize: 14, maxWidth: 420, mb: 2, fontFamily: "'Poppins', sans-serif" }}>
                  Create your first rule to trigger chatbot actions on inbound messages.
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateRuleDialog} sx={{ borderRadius: 2, textTransform: "none" }}>
                  Create Rule
                </Button>
              </Box>
            ) : (
              <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Rule Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Trigger</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Stop on Match</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Executed</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rules.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell>{rule.rule_name}</TableCell>
                        <TableCell>
                          <Chip size="small" label={rule.trigger_type} variant="outlined" />
                        </TableCell>
                        <TableCell>{rule.priority}</TableCell>
                        <TableCell>{rule.stop_on_match ? "Yes" : "No"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={rule.enabled ? "On" : "Off"}
                            color={rule.enabled ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell>{rule.execution_count || 0}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEditRuleDialog(rule)} sx={{ color: "#1976d2" }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => openDeleteRuleDialog(rule)} sx={{ color: "#d32f2f" }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
            {ruleLogs.length > 0 && (
              <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", mt: 2 }}>
                <Box sx={{ p: 2, borderBottom: "1px solid #eee" }}>
                  <Typography sx={{ fontWeight: 600 }}>Recent Rule Logs</Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Rule</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Matched</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Executed</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ruleLogs.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.rule_id}</TableCell>
                        <TableCell>{row.phone_number || "-"}</TableCell>
                        <TableCell>{row.event_type}</TableCell>
                        <TableCell>{row.matched ? "Yes" : "No"}</TableCell>
                        <TableCell>{row.executed ? "Yes" : "No"}</TableCell>
                        <TableCell>{row.reason || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </Box>
        )}

        {activeTab === "keyword-actions" && (
          <Box sx={{ mb: 4 }}>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
              {/* Action Bar */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 4,
                  gap: 3,
                }}
              >
             <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: "#1976d2", width: 40, height: 40 }}>
              <KeyOutlinedIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: "#333", fontFamily: 'Poppins, sans-serif' }}>
                Keyword Actions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Poppins, sans-serif" }}>
                Legacy keyword action is deprecated. Configure your keywords in{" "}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => handleTabChange("rules")}
                  sx={{
                    color: "#1976d2",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    verticalAlign: "baseline",
                    cursor: "pointer",
                    p: 0,
                    border: 0,
                    bgcolor: "transparent",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Rules
                </Link>
                .
              </Typography>
            </Box>
          </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {/* <Button
                    variant="outlined"
                    onClick={() => setIsFallbackDialogOpen(true)}
                    disabled
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      textTransform: "none",
                      fontSize: "14px",
                      fontWeight: 500,
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  >
                    Fallback action
                  </Button> */}
                  <Button
                    variant="contained"
                    onClick={handleOpenAddKeywordDialog}
                    disabled
                    sx={{
                      backgroundColor: "#1976d2",
                      "&:hover": { backgroundColor: "#1565c0" },
                      borderRadius: 2,
                      px: 3,
                      textTransform: "none",
                      fontSize: "14px",
                      fontWeight: 500,
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  >
                    Add keyword action
                  </Button>
                </Box>
              </Box>
            </Box>
{/* ===== EMPTY KEYWORD ACTIONS PLACEHOLDER ===== */}
{keywordActions.length === 0 && (
  <Box
    sx={{
      height: 600,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      textAlign: "center",
      color: "#9ca3af",
    }}
  >
    <KeyOutlinedIcon sx={{ fontSize: 64, mb: 1.5 }} />

    <Typography
      sx={{
        fontSize: 18,
        fontWeight: 600,
        color: "#374151",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      No Keyword Actions Yet
    </Typography>

    <Typography
      sx={{
        fontSize: 14,
        maxWidth: 360,
        mb: 2,
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      Add keywords to automatically reply when users send matching messages
    </Typography>

    <Button
      variant="contained"
      onClick={() => handleOpenAddKeywordDialog()}
      disabled
      sx={{ borderRadius: 2, textTransform: "none" }}
    >
      Add Keyword Action
    </Button>
  </Box>
)}

            {/* Keyword Actions Table */}
            {keywordActions.length > 0 && (
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <Box sx={{  maxHeight: "60vh",
               overflowY: "auto",
               overflowX: "auto", }}>
                <Table stickyHeader >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          backgroundColor: "#f5f5f5",
                          color: "#374151",
                          fontSize: "14px",
                          py: 2.5,
                          px: 3,
                          fontFamily: "'Poppins', sans-serif",
                        }}
                      >
                        Keyword
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          backgroundColor: "#f5f5f5",
                          fontSize: "14px",
                          py: 2.5,
                          px: 3,
                          fontFamily: "'Poppins', sans-serif",
                        }}
                      >
                        Triggered
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          backgroundColor: "#f5f5f5",
                          fontSize: "14px",
                          py: 2.5,
                          px: 3,
                          fontFamily: "'Poppins', sans-serif",
                        }}
                      >
                        Matching method
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          backgroundColor: "#f5f5f5",
                          fontSize: "14px",
                          py: 2.5,
                          px: 3,
                          fontFamily: "'Poppins', sans-serif",
                        }}
                      >
                        Reply material
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          backgroundColor: "#f5f5f5",
                          fontSize: "14px",
                          py: 2.5,
                          px: 3,
                          fontFamily: "'Poppins', sans-serif",
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {keywordActions.map((action) => (
                      <TableRow
                        key={action.id}
                        sx={{
                          "&:hover": { backgroundColor: "#f8f9fa" },
                          "&:last-child td": { border: 0 },
                        }}
                      >
                        <TableCell sx={{ py: 2.5, px: 3 }}>
                          <Chip
                            label={action.keyword}
                            size="small"
                            sx={{
                              backgroundColor: "#e5e7eb",
                              color: "#6b7280",
                              fontWeight: 500,
                              fontSize: "12px",
                              height: "24px",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 2.5, px: 3 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, fontSize: "14px" }}
                          >
                            {action.triggered}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2.5, px: 3 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "13px" }}
                          >
                            {action.matchingMethod}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2.5, px: 3 }}>
                          <Chip
                            label={action.replyMaterial}
                            size="small"
                            sx={{
                              backgroundColor: "#fff3e0",
                              color: "#f57c00",
                              fontWeight: 500,
                              fontSize: "12px",
                              height: "24px",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 2.5, px: 3 }}>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton
                              size="small"
                              sx={{
                                color: "#1976d2",
                                "&:hover": { backgroundColor: "#e3f2fd" },
                              }}
                              onClick={() => handleOpenAddKeywordDialog(action)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{
                                color: "#f44336",
                                "&:hover": { backgroundColor: "#ffebee" },
                              }}
                              onClick={() => handleDeleteKeywordAction(action)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Card>
            )}
          </Box>
        )}

        {/* Forms list */}
        {activeTab === "forms" && forms.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {forms.map((form) => (
                    <TableRow key={form.id}>
                      <TableCell>{form.name || "-"}</TableCell>
                      <TableCell>
                        <Chip label={form.type} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip label={form.status || "draft"} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit form">
                          <IconButton size="small" onClick={() => handleOpenFormDialog(form)} sx={{ color: "#1976d2" }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete form">
                          <IconButton size="small" onClick={() => openDeleteFormDialog(form)} sx={{ color: "#d32f2f" }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </Box>
        )}

        {/* Your chatbots (grid) — Library tab uses ChatbotLibraryPanel above */}
        {activeTab === "chatbots" && chatbotsListTab === "yours" && automations.length > 0 && (
          <Box sx={{ overflowX: "auto", overflowY: "auto", p: 2 }}>
            <Grid container spacing={3}>
              {filteredYourChatbots.map((automation) => (
                <Grid item xs={12} md={6} lg={4} key={automation.getId()}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      mt: 1,
                      ml: 1,
                      boxShadow: "1px 2px 8px rgba(0,0,0,0.1)",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {automation.getTemplateName()}
                        </Typography>
                        <Chip
                          icon={getStatusIcon(automation.getStatus())}
                          label={automation.getStatus()}
                          color={getStatusColor(automation.getStatus())}
                          size="small"
                        />
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {automation.getDescription()}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <ScheduleIcon sx={{ fontSize: 16, color: "#666" }} />
                          <Typography variant="body2" color="text.secondary">
                            Trigger: {automation.getTrigger()}
                          </Typography>
                        </Box>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <MessageIcon sx={{ fontSize: 16, color: "#666" }} />
                          <Typography variant="body2" color="text.secondary">
                            Action: {automation.getAction()}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {automation.getContacts()} contacts
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Last run: {automation.getLastRun()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Next: {automation.getNextRun()}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
                        <Tooltip title="Open Visual Flow Builder to edit automation">
                          <IconButton
                            size="small"
                            onClick={() => handleEditAutomation(automation)}
                            sx={{ color: "#1976d2" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            automation.status === "active"
                              ? "Pause"
                              : "Activate"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(automation)}
                            sx={{
                              color:
                                automation.status === "active"
                                  ? "#ff9800"
                                  : "#4caf50",
                            }}
                          >
                            {automation.status === "active" ? (
                              <PauseIcon fontSize="small" />
                            ) : (
                              <PlayIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Automation">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteAutomation(automation)}
                            sx={{ color: "#f44336" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {activeTab === "chatbots" && chatbotsListTab === "library" && (
          <Box sx={{ width: "100%" }}>
            <ChatbotLibraryPanel
              onAfterClone={fetch}
              previewFrom="/automation/chatbots?view=library"
            />
          </Box>
        )}

        {activeTab === "chatbots" &&
          chatbotsListTab === "yours" &&
          automations.length > 0 &&
          filteredYourChatbots.length === 0 && (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography color="text.secondary" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                No chatbots match your search.
              </Typography>
            </Box>
          )}
      </Box>

      {/* Create/Edit Rule Dialog */}
      <Dialog
        open={isRuleDialogOpen}
        onClose={() => setIsRuleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingRuleId ? "Edit Rule" : "Create Rule"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Rule name"
            value={ruleForm.rule_name}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, rule_name: e.target.value }))}
            error={!!ruleErrors.rule_name}
            helperText={ruleErrors.rule_name}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Trigger type</InputLabel>
            <Select
              label="Trigger type"
              value={ruleForm.trigger_type}
              onChange={(e) => {
                const nextTrigger = e.target.value;
                if (nextTrigger === "platform_event") {
                  setRuleForm((prev) => ({
                    ...prev,
                    trigger_type: nextTrigger,
                    filters: {
                      logic: "all",
                      conditions: [
                        { field: "platform", operator: "equals", value: "shopify" },
                        { field: "event_type", operator: "equals", value: "orders/create" },
                      ],
                      trigger_delay_minutes: 60,
                    },
                    actions: prev.actions?.[0]?.type === "send_template"
                      ? prev.actions
                      : [{ type: "send_template", template_id: "", variable_paths: { header: {}, body: {} } }],
                  }));
                } else if (nextTrigger === "order_received") {
                  setRuleForm((prev) => ({
                    ...prev,
                    trigger_type: nextTrigger,
                    filters: { logic: "all", conditions: [] },
                    actions: prev.actions?.[0]?.type === "send_chatbot"
                      ? [{
                        ...prev.actions[0],
                        type: "send_chatbot",
                        flow_context: prev.actions?.[0]?.flow_context || "outside_flow_only",
                      }]
                      : [{ type: "send_chatbot", chatbot_id: "", flow_context: "outside_flow_only" }],
                  }));
                } else if (nextTrigger === "image_received") {
                  setRuleForm((prev) => ({
                    ...prev,
                    trigger_type: nextTrigger,
                    filters: { logic: "all", conditions: [] },
                    actions: prev.actions?.[0]?.type === "visual_product_search"
                      ? prev.actions
                      : [{
                        type: "visual_product_search",
                        flow_context: "outside_flow_only",
                        fallback_text: "We could not find a matching product. Please send another photo.",
                        expand_variants: true,
                        max_variants: 30,
                        similarity_threshold: 0.85,
                      }],
                  }));
                } else if (nextTrigger === LEAD_STAGE_FOLLOWUP_TRIGGER) {
                  const defaultStageId = pipelineStages[0]?.id || "";
                  const nextFilters = getDefaultLeadStageFollowupFilters(defaultStageId);
                  setAbandonedScheduleDraft(syncAbandonedScheduleDraftFromFilters(nextFilters));
                  setRuleForm((prev) => ({
                    ...prev,
                    trigger_type: nextTrigger,
                    filters: nextFilters,
                    actions: [{ type: "send_template", template_id: "", variable_paths: { header: {}, body: {} } }],
                  }));
                } else if (nextTrigger === PHONE_CALL_INCOMING_TRIGGER) {
                  setRuleForm((prev) => ({
                    ...prev,
                    trigger_type: nextTrigger,
                    filters: { logic: "all", conditions: [] },
                    actions: [{ type: "send_template", template_id: "", variable_paths: { header: {}, body: {} } }],
                  }));
                } else if (nextTrigger === PHONE_CALL_ENDED_TRIGGER) {
                  setRuleForm((prev) => ({
                    ...prev,
                    trigger_type: nextTrigger,
                    filters: getDefaultPhoneCallEndedFilters(""),
                    actions: [{ type: "send_template", template_id: "", variable_paths: { header: {}, body: {} } }],
                  }));
                } else {
                  setRuleForm((prev) => ({
                    ...prev,
                    trigger_type: nextTrigger,
                    filters: prev.filters?.conditions?.length
                      ? prev.filters
                      : { logic: "all", conditions: [{ field: "keyword", operator: "contains", value: "", match_method: "CONTAINS" }] },
                  }));
                }
              }}
            >
              <MenuItem value="message_received">Message received</MenuItem>
              <MenuItem value="text_received">Text message received</MenuItem>
              <MenuItem value="interactive_received">Interactive reply received</MenuItem>
              <MenuItem value="image_received">Customer sends image</MenuItem>
              <MenuItem value="order_received">Customer sends catalog order</MenuItem>
              <MenuItem value="platform_event">Platform event (order automation)</MenuItem>
              <MenuItem value={LEAD_STAGE_FOLLOWUP_TRIGGER}>Lead stage follow-up</MenuItem>
              <MenuItem value={PHONE_CALL_INCOMING_TRIGGER}>Phone call — incoming received</MenuItem>
              <MenuItem value={PHONE_CALL_ENDED_TRIGGER}>Phone call — call ended</MenuItem>
            </Select>
          </FormControl>
          {(() => {
            const overlapHint = getAutomationTriggerOverlapHint({
              triggerType: ruleForm.trigger_type,
              rules,
              editingRuleId,
            });
            return overlapHint ? (
              <Typography variant="caption" color="warning.main" display="block" sx={{ mt: -0.5, mb: 1 }}>
                {overlapHint}
              </Typography>
            ) : null;
          })()}
          <TextField
            label="Priority"
            type="number"
            value={ruleForm.priority}
            onChange={(e) => setRuleForm((prev) => ({ ...prev, priority: Number(e.target.value || 100) }))}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={ruleForm.enabled}
                onChange={(e) => setRuleForm((prev) => ({ ...prev, enabled: e.target.checked }))}
              />
            }
            label="Enabled"
          />
          <FormControlLabel
            control={
              <Switch
                checked={ruleForm.stop_on_match}
                onChange={(e) => setRuleForm((prev) => ({ ...prev, stop_on_match: e.target.checked }))}
              />
            }
            label="Stop processing next rules after this rule matches"
          />
          <FormControl fullWidth>
            <InputLabel>Action type</InputLabel>
            <Select
              label="Action type"
              value={ruleForm.actions[0]?.type || ((isLeadStageFollowupTrigger(ruleForm.trigger_type) || isPhoneCallAutomationTrigger(ruleForm.trigger_type)) ? "send_template" : "send_chatbot")}
              disabled={isLeadStageFollowupTrigger(ruleForm.trigger_type) || isPhoneCallAutomationTrigger(ruleForm.trigger_type)}
              onChange={(e) => {
                const nextType = e.target.value;
                let nextAction = {
                  type: "send_chatbot",
                  chatbot_id: "",
                  ...(ruleForm.trigger_type === "order_received" ? { flow_context: "outside_flow_only" } : {}),
                };
                if (nextType === "send_text") nextAction = { type: "send_text", text: "" };
                if (nextType === "assign_to_account") nextAction = { type: "assign_to_account", account_id: "" };
                if (nextType === "add_tag") nextAction = { type: "add_tag", tag_id: "" };
                if (nextType === "send_template") nextAction = { type: "send_template", template_id: "", variable_paths: { header: {}, body: {} } };
                if (nextType === "ai_autoreply") {
                  nextAction = {
                    type: "ai_autoreply",
                    ai_provider_key: "openai",
                    ai_persona_preset_id: "",
                    ai_session_hours: 1,
                  };
                }
                if (nextType === "visual_product_search") {
                  nextAction = {
                    type: "visual_product_search",
                    flow_context: "outside_flow_only",
                    fallback_text: "We could not find a matching product. Please send another photo.",
                    expand_variants: true,
                    max_variants: 30,
                    similarity_threshold: 0.85,
                  };
                }
                setRuleForm((prev) => ({ ...prev, actions: [nextAction] }));
              }}
            >
              <MenuItem value="send_chatbot">Send chatbot flow</MenuItem>
              <MenuItem value="send_text">Send text message</MenuItem>
              <MenuItem value="assign_to_account">Assign conversation</MenuItem>
              <MenuItem value="add_tag">Add tag</MenuItem>
              <MenuItem value="send_template">Send template (event)</MenuItem>
              <MenuItem value="ai_autoreply">AI auto-reply</MenuItem>
              <MenuItem value="visual_product_search">Visual product search (CLIP)</MenuItem>
            </Select>
          </FormControl>
          {(ruleForm.actions[0]?.type || "send_chatbot") === "send_chatbot" ? (
            <>
            <FormControl fullWidth error={!!ruleErrors.actions}>
              <InputLabel>Chatbot</InputLabel>
              <Select
                label="Chatbot"
                value={ruleForm.actions[0]?.chatbot_id || ""}
                onChange={(e) =>
                  setRuleForm((prev) => ({
                    ...prev,
                    actions: [{
                      ...(prev.actions?.[0] || {}),
                      type: "send_chatbot",
                      chatbot_id: e.target.value,
                    }],
                  }))
                }
              >
                {chatbots.map((bot) => (
                  <MenuItem key={bot.id} value={bot.id}>
                    {bot.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {ruleForm.trigger_type === "order_received" && (
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel>When to run</InputLabel>
                <Select
                  label="When to run"
                  value={ruleForm.actions[0]?.flow_context || "outside_flow_only"}
                  onChange={(e) =>
                    setRuleForm((prev) => ({
                      ...prev,
                      actions: [{
                        ...(prev.actions?.[0] || {}),
                        type: "send_chatbot",
                        flow_context: e.target.value,
                      }],
                    }))
                  }
                >
                  <MenuItem value="outside_flow_only">Outside active chatbot step only</MenuItem>
                  <MenuItem value="inside_flow_only">During active chatbot step only</MenuItem>
                  <MenuItem value="always">Any time (advanced)</MenuItem>
                </Select>
              </FormControl>
            )}
            </>
          ) : (ruleForm.actions[0]?.type || "send_chatbot") === "assign_to_account" ? (
            <FormControl fullWidth error={!!ruleErrors.actions}>
              <InputLabel>Assign to account</InputLabel>
              <Select
                label="Assign to account"
                value={ruleForm.actions[0]?.account_id || ""}
                onChange={(e) =>
                  setRuleForm((prev) => ({ ...prev, actions: [{ type: "assign_to_account", account_id: e.target.value }] }))
                }
              >
                {routingStaff.map((staff) => (
                  <MenuItem key={staff.id || staff.account_id} value={staff.account_id || staff.id}>
                    {staff.name || staff.full_name || staff.email || staff.account_id || staff.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (ruleForm.actions[0]?.type || "send_chatbot") === "add_tag" ? (
            <FormControl fullWidth error={!!ruleErrors.actions}>
              <InputLabel>Tag</InputLabel>
              <Select
                label="Tag"
                value={ruleForm.actions[0]?.tag_id || ""}
                onChange={(e) =>
                  setRuleForm((prev) => ({ ...prev, actions: [{ type: "add_tag", tag_id: e.target.value }] }))
                }
              >
                {tags.map((tag) => (
                  <MenuItem key={tag.getId ? tag.getId() : tag.id} value={tag.getId ? tag.getId() : tag.id}>
                    {tag.getName ? tag.getName() : tag.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (ruleForm.actions[0]?.type || "send_chatbot") === "send_template" ? (
            <>
              <FormControl fullWidth error={!!ruleErrors.actions}>
                <InputLabel>Template</InputLabel>
                <Select
                  label="Template"
                  value={ruleForm.actions[0]?.template_id || ""}
                  onChange={(e) =>
                    setRuleForm((prev) => ({
                      ...prev,
                      actions: [{
                        type: "send_template",
                        template_id: e.target.value,
                        variable_paths: { header: {}, body: {} },
                      }],
                    }))
                  }
                >
                  {messageTemplates.map((t) => (
                    <MenuItem key={t.getId()} value={t.getId()}>
                      {t.attrs?.templateName || t.getId()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {ruleForm.trigger_type === "platform_event" && (() => {
                const platform = (ruleForm.filters?.conditions || []).find((c) => c.field === "platform")?.value || "shopify";
                const currentEventForVars = (ruleForm.filters?.conditions || []).find((c) => c.field === "event_type")?.value || "";
                const payloadFields = isAbandonedCartPlatformEvent(currentEventForVars)
                  ? (platform === "estore" ? ABANDONED_ESTORE_PAYLOAD_FIELDS : ABANDONED_SHOPIFY_PAYLOAD_FIELDS)
                  : (platform === "estore" ? ESTORE_PAYLOAD_FIELDS : SHOPIFY_PAYLOAD_FIELDS);
                const tmpl = messageTemplates.find((t) => t.getId() === ruleForm.actions[0]?.template_id);
                const vars = extractTemplateVariables(tmpl?.attrs?.body || "");
                if (!tmpl || vars.length === 0) return null;
                return (
                  <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "grey.50", borderRadius: 1, border: "1px solid", borderColor: "grey.200" }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Map template variables to payload fields
                    </Typography>
                    {vars.map((varKey) => (
                      <Box key={varKey} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Typography variant="body2" sx={{ minWidth: 36, fontFamily: "monospace", color: "primary.main", fontWeight: 700 }}>
                          {`{{${varKey}}}`}
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            displayEmpty
                            value={ruleForm.actions[0]?.variable_paths?.body?.[varKey] || ""}
                            onChange={(e) =>
                              setRuleForm((prev) => {
                                const vp = { ...(prev.actions[0]?.variable_paths || { header: {}, body: {} }) };
                                vp.body = { ...(vp.body || {}), [varKey]: e.target.value };
                                return { ...prev, actions: [{ ...prev.actions[0], variable_paths: vp }] };
                              })
                            }
                          >
                            <MenuItem value=""><em>Select field…</em></MenuItem>
                            {payloadFields.map((f) => (
                              <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    ))}
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {isAbandonedCartPlatformEvent(currentEventForVars)
                        ? "Fields from the abandoned-cart payload when the reminder is sent."
                        : platform === "estore"
                          ? "Fields from the Orka Stores order payload sent with each status update."
                          : "Fields from the Shopify order payload sent with each event."}
                    </Typography>
                  </Box>
                );
              })()}
              {isLeadStageFollowupTrigger(ruleForm.trigger_type) && (() => {
                const tmpl = messageTemplates.find((t) => t.getId() === ruleForm.actions[0]?.template_id);
                const vars = extractTemplateVariables(tmpl?.attrs?.body || "");
                if (!tmpl || vars.length === 0) return null;
                return (
                  <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "grey.50", borderRadius: 1, border: "1px solid", borderColor: "grey.200" }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Map template variables to lead fields
                    </Typography>
                    {vars.map((varKey) => (
                      <Box key={varKey} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Typography variant="body2" sx={{ minWidth: 36, fontFamily: "monospace", color: "primary.main", fontWeight: 700 }}>
                          {`{{${varKey}}}`}
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            displayEmpty
                            value={ruleForm.actions[0]?.variable_paths?.body?.[varKey] || ""}
                            onChange={(e) =>
                              setRuleForm((prev) => {
                                const vp = { ...(prev.actions[0]?.variable_paths || { header: {}, body: {} }) };
                                vp.body = { ...(vp.body || {}), [varKey]: e.target.value };
                                return { ...prev, actions: [{ ...prev.actions[0], variable_paths: vp }] };
                              })
                            }
                          >
                            <MenuItem value=""><em>Select field…</em></MenuItem>
                            {LEAD_PAYLOAD_FIELDS.map((f) => (
                              <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    ))}
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      Fields from the lead payload when the follow-up template is sent.
                    </Typography>
                  </Box>
                );
              })()}
              {isPhoneCallAutomationTrigger(ruleForm.trigger_type) && (() => {
                const tmpl = messageTemplates.find((t) => t.getId() === ruleForm.actions[0]?.template_id);
                const vars = extractTemplateVariables(tmpl?.attrs?.body || "");
                if (!tmpl || vars.length === 0) return null;
                return (
                  <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "grey.50", borderRadius: 1, border: "1px solid", borderColor: "grey.200" }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Map template variables to phone call fields
                    </Typography>
                    {vars.map((varKey) => (
                      <Box key={varKey} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Typography variant="body2" sx={{ minWidth: 36, fontFamily: "monospace", color: "primary.main", fontWeight: 700 }}>
                          {`{{${varKey}}}`}
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            displayEmpty
                            value={ruleForm.actions[0]?.variable_paths?.body?.[varKey] || ""}
                            onChange={(e) =>
                              setRuleForm((prev) => {
                                const vp = { ...(prev.actions[0]?.variable_paths || { header: {}, body: {} }) };
                                vp.body = { ...(vp.body || {}), [varKey]: e.target.value };
                                return { ...prev, actions: [{ ...prev.actions[0], variable_paths: vp }] };
                              })
                            }
                          >
                            <MenuItem value=""><em>Select field…</em></MenuItem>
                            {PHONE_CALL_PAYLOAD_FIELDS.map((f) => (
                              <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    ))}
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {isPhoneCallIncomingTrigger(ruleForm.trigger_type)
                        ? "Sent when the mobile dialer reports an incoming call (ring)."
                        : "Sent when the mobile dialer logs a completed call."}
                    </Typography>
                  </Box>
                );
              })()}
            </>
          ) : (ruleForm.actions[0]?.type || "send_chatbot") === "visual_product_search" ? (
            <>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel>When to run</InputLabel>
                <Select
                  label="When to run"
                  value={ruleForm.actions[0]?.flow_context || "outside_flow_only"}
                  onChange={(e) =>
                    setRuleForm((prev) => ({
                      ...prev,
                      actions: [{ ...(prev.actions?.[0] || {}), type: "visual_product_search", flow_context: e.target.value }],
                    }))
                  }
                >
                  <MenuItem value="outside_flow_only">Outside active chatbot step only</MenuItem>
                  <MenuItem value="inside_flow_only">During active chatbot step only</MenuItem>
                  <MenuItem value="always">Any time (advanced)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Fallback message (no match)"
                multiline
                minRows={2}
                value={ruleForm.actions[0]?.fallback_text || ""}
                onChange={(e) =>
                  setRuleForm((prev) => ({
                    ...prev,
                    actions: [{ ...(prev.actions?.[0] || {}), type: "visual_product_search", fallback_text: e.target.value }],
                  }))
                }
                fullWidth
              />
              <TextField
                label="Similarity threshold (0–1)"
                type="number"
                inputProps={{ min: 0.5, max: 0.99, step: 0.01 }}
                value={ruleForm.actions[0]?.similarity_threshold ?? 0.85}
                onChange={(e) =>
                  setRuleForm((prev) => ({
                    ...prev,
                    actions: [{
                      ...(prev.actions?.[0] || {}),
                      type: "visual_product_search",
                      similarity_threshold: Number(e.target.value),
                    }],
                  }))
                }
                fullWidth
              />
              <TextField
                label="Max variants in product list"
                type="number"
                inputProps={{ min: 1, max: 30 }}
                value={ruleForm.actions[0]?.max_variants ?? 30}
                onChange={(e) =>
                  setRuleForm((prev) => ({
                    ...prev,
                    actions: [{
                      ...(prev.actions?.[0] || {}),
                      type: "visual_product_search",
                      max_variants: Number(e.target.value),
                    }],
                  }))
                }
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={ruleForm.actions[0]?.expand_variants !== false}
                    onChange={(e) =>
                      setRuleForm((prev) => ({
                        ...prev,
                        actions: [{
                          ...(prev.actions?.[0] || {}),
                          type: "visual_product_search",
                          expand_variants: e.target.checked,
                        }],
                      }))
                    }
                  />
                }
                label="Expand all sizes (S/M/L) when image is shared"
              />
            </>
          ) : (ruleForm.actions[0]?.type || "send_chatbot") === "ai_autoreply" ? (
            <>
              <FormControl fullWidth error={!!ruleErrors.actions} sx={{ mt: 1 }}>
                <InputLabel>AI Provider</InputLabel>
                <Select
                  label="AI Provider"
                  value={ruleForm.actions[0]?.ai_provider_key || "openai"}
                  onChange={(e) =>
                    setRuleForm((prev) => ({
                      ...prev,
                      actions: [{
                        ...(prev.actions?.[0] || {}),
                        type: "ai_autoreply",
                        ai_provider_key: e.target.value,
                      }],
                    }))
                  }
                >
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="gemini">Gemini</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth error={!!ruleErrors.actions}>
                <InputLabel>Persona</InputLabel>
                <Select
                  label="Persona"
                  value={ruleForm.actions[0]?.ai_persona_preset_id || ""}
                  onChange={(e) =>
                    setRuleForm((prev) => ({
                      ...prev,
                      actions: [{
                        ...(prev.actions?.[0] || {}),
                        type: "ai_autoreply",
                        ai_persona_preset_id: e.target.value,
                      }],
                    }))
                  }
                >
                  {aiPersonas.map((p) => (
                    <MenuItem key={p.id} value={p.persona_key || p.id}>
                      {p.title} ({p.persona_key || p.id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="AI reply duration (hours)"
                type="number"
                inputProps={{ min: 1, step: 1 }}
                value={ruleForm.actions[0]?.ai_session_hours ?? 1}
                onChange={(e) =>
                  setRuleForm((prev) => ({
                    ...prev,
                    actions: [{
                      ...(prev.actions?.[0] || {}),
                      type: "ai_autoreply",
                      ai_session_hours: Number(e.target.value || 1),
                    }],
                  }))
                }
                helperText="After this duration, conversation is auto-assigned to owner until bot is resumed."
                fullWidth
              />
              {!!ruleErrors.actions && (
                <Typography variant="caption" color="error">{ruleErrors.actions}</Typography>
              )}
            </>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 1 }}>
                Appointment self-service templates can be used after appointment events so customers can cancel or reschedule inside WhatsApp.
              </Alert>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                {APPOINTMENT_SELF_SERVICE_TEMPLATES.map((template) => (
                  <Button
                    key={template.id}
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setRuleForm((prev) => ({ ...prev, actions: [{ type: "send_text", text: template.text }] }))
                    }
                  >
                    {template.label}
                  </Button>
                ))}
              </Stack>
              <TextField
                label="Message text"
                multiline
                minRows={3}
                value={ruleForm.actions[0]?.text || ""}
                onChange={(e) =>
                  setRuleForm((prev) => ({ ...prev, actions: [{ type: "send_text", text: e.target.value }] }))
                }
                error={!!ruleErrors.actions}
                helperText={ruleErrors.actions}
                fullWidth
              />
            </Box>
          )}
          {isPhoneCallEndedTrigger(ruleForm.trigger_type) && (
            <FormControl fullWidth>
              <InputLabel>Call direction filter</InputLabel>
              <Select
                label="Call direction filter"
                value={(ruleForm.filters?.conditions || []).find((c) => c.field === "call_type")?.value || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setRuleForm((prev) => ({
                    ...prev,
                    filters: getDefaultPhoneCallEndedFilters(value),
                  }));
                }}
              >
                <MenuItem value="">Any direction</MenuItem>
                <MenuItem value="incoming">Incoming only</MenuItem>
                <MenuItem value="outgoing">Outgoing only</MenuItem>
                <MenuItem value="missed">Missed only</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Optional. Limit this rule to incoming, outgoing, or missed calls when the call ends.
              </Typography>
            </FormControl>
          )}
          {isPhoneCallIncomingTrigger(ruleForm.trigger_type) && (
            <Typography variant="body2" color="text.secondary">
              Fires immediately when the Android dialer calls POST /calls/:service_id/start with call_type incoming.
            </Typography>
          )}
          {isLeadStageFollowupTrigger(ruleForm.trigger_type) && (() => {
            const followUpEnabled = isAbandonedCartFollowUpEnabled(ruleForm.filters);
            const currentStageId = (ruleForm.filters?.conditions || []).find((c) => c.field === "stage_id")?.value || "";
            const previewFilters = applyAbandonedScheduleDraftToFilters(
              ruleForm.filters,
              abandonedScheduleDraft,
            );
            const estimatedReminders = estimatedAbandonedCartReminders(previewFilters);
            return (
              <>
                <FormControl fullWidth error={!!ruleErrors.filters}>
                  <InputLabel>Pipeline stage</InputLabel>
                  <Select
                    label="Pipeline stage"
                    value={currentStageId}
                    onChange={(e) => {
                      const nextStageId = e.target.value;
                      setRuleForm((prev) => ({
                        ...prev,
                        filters: {
                          ...(prev.filters || { logic: "all", conditions: [] }),
                          conditions: [{ field: "stage_id", operator: "equals", value: nextStageId }],
                        },
                      }));
                    }}
                  >
                    {pipelineStages.length === 0 ? (
                      <MenuItem value="" disabled>No stages available</MenuItem>
                    ) : (
                      pipelineStages.map((stage) => (
                        <MenuItem key={stage.id} value={stage.id}>
                          {stage.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <TextField
                  label="Send first template after (minutes)"
                  type="text"
                  inputMode="numeric"
                  fullWidth
                  value={abandonedScheduleDraft.triggerDelayMinutes}
                  onChange={(e) => {
                    setAbandonedScheduleDraft((prev) => ({
                      ...prev,
                      triggerDelayMinutes: sanitizeNumericDraftInput(e.target.value),
                    }));
                  }}
                  helperText="Time after the lead enters this stage before the first template (5 min – 7 days)."
                  error={!!ruleErrors.filters}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={followUpEnabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setRuleForm((prev) => ({
                          ...prev,
                          filters: {
                            ...(prev.filters || { logic: "all", conditions: [] }),
                            trigger_delay_minutes: prev.filters?.trigger_delay_minutes ?? 60,
                            reminder_interval_minutes: enabled ? (prev.filters?.reminder_interval_minutes ?? 2880) : null,
                            max_reminder_days: enabled ? (prev.filters?.max_reminder_days ?? 7) : null,
                            max_reminder_count: enabled ? (prev.filters?.max_reminder_count ?? null) : null,
                          },
                        }));
                        if (enabled) {
                          setAbandonedScheduleDraft((prev) => ({
                            ...prev,
                            intervalDays: prev.intervalDays || "2",
                            maxReminderDays: prev.maxReminderDays || "7",
                          }));
                        }
                      }}
                    />
                  }
                  label="Send follow-up templates"
                />
                {followUpEnabled && (
                  <>
                    <TextField
                      label="Repeat every (days)"
                      type="text"
                      inputMode="numeric"
                      fullWidth
                      value={abandonedScheduleDraft.intervalDays}
                      onChange={(e) => {
                        setAbandonedScheduleDraft((prev) => ({
                          ...prev,
                          intervalDays: sanitizeNumericDraftInput(e.target.value),
                        }));
                      }}
                      helperText="Gap between follow-up templates after the previous send (24 hours per day)."
                      error={!!ruleErrors.filters}
                    />
                    <TextField
                      label="Stop sending after (days)"
                      type="text"
                      inputMode="numeric"
                      fullWidth
                      value={abandonedScheduleDraft.maxReminderDays}
                      onChange={(e) => {
                        setAbandonedScheduleDraft((prev) => ({
                          ...prev,
                          maxReminderDays: sanitizeNumericDraftInput(e.target.value),
                        }));
                      }}
                      helperText={`Campaign window from when the lead enters this stage (max ${ABANDONED_CART_MAX_REMINDER_DAYS} days).`}
                      error={!!ruleErrors.filters}
                    />
                    <TextField
                      label="Max templates (optional)"
                      type="text"
                      inputMode="numeric"
                      fullWidth
                      value={abandonedScheduleDraft.maxReminderCount}
                      onChange={(e) => {
                        setAbandonedScheduleDraft((prev) => ({
                          ...prev,
                          maxReminderCount: sanitizeNumericDraftInput(e.target.value),
                        }));
                      }}
                      helperText={`Leave empty to auto-calculate. Up to ~${estimatedReminders} send(s) with current settings.`}
                      error={!!ruleErrors.filters}
                    />
                  </>
                )}
              </>
            );
          })()}
          {ruleForm.trigger_type === "platform_event" ? (
            <>
              {(() => {
                const platform = (ruleForm.filters?.conditions || []).find((c) => c.field === "platform")?.value || "shopify";
                const eventOptions = platformEventsForPlatform(platform);
                const defaultEvent = defaultEventForPlatform(platform);
                const currentEvent = (ruleForm.filters?.conditions || []).find((c) => c.field === "event_type")?.value || defaultEvent;
                const isAbandonedEvent = isAbandonedCartPlatformEvent(currentEvent);
                const followUpEnabled = isAbandonedCartFollowUpEnabled(ruleForm.filters);
                const previewFilters = applyAbandonedScheduleDraftToFilters(
                  ruleForm.filters,
                  abandonedScheduleDraft,
                );
                const estimatedReminders = estimatedAbandonedCartReminders(previewFilters);
                return (
                  <>
              <FormControl fullWidth error={!!ruleErrors.filters}>
                <InputLabel>Platform</InputLabel>
                <Select
                  label="Platform"
                  value={platform}
                  onChange={(e) => {
                    const nextPlatform = e.target.value;
                    const nextDefaultEvent = defaultEventForPlatform(nextPlatform);
                    const nextIsAbandoned = isAbandonedCartPlatformEvent(nextDefaultEvent);
                    setRuleForm((prev) => {
                      const nextFilters = {
                        logic: "all",
                        conditions: [
                          { field: "platform", operator: "equals", value: nextPlatform },
                          { field: "event_type", operator: "equals", value: nextDefaultEvent },
                        ],
                        ...(nextIsAbandoned ? getAbandonedCartSchedulePatch(prev.filters) : {}),
                      };
                      if (nextIsAbandoned) {
                        setAbandonedScheduleDraft(syncAbandonedScheduleDraftFromFilters(nextFilters));
                      }
                      return {
                        ...prev,
                        filters: nextFilters,
                        actions: nextIsAbandoned
                          ? [{ type: "send_template", template_id: prev.actions?.[0]?.template_id || "", variable_paths: prev.actions?.[0]?.variable_paths || { header: {}, body: {} } }]
                          : prev.actions,
                      };
                    });
                  }}
                >
                  <MenuItem value="shopify">Shopify</MenuItem>
                  <MenuItem value="estore">Orka Stores (estore)</MenuItem>
                  <MenuItem value="whatsapp_vertical">WhatsApp booking vertical</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth error={!!ruleErrors.filters}>
                <InputLabel>Event</InputLabel>
                <Select
                  label="Event"
                  value={currentEvent}
                  onChange={(e) => {
                    const nextEvent = e.target.value;
                    const nextIsAbandoned = isAbandonedCartPlatformEvent(nextEvent);
                    setRuleForm((prev) => {
                      const nextFilters = {
                        logic: "all",
                        conditions: [
                          { field: "platform", operator: "equals", value: platform },
                          { field: "event_type", operator: "equals", value: nextEvent },
                        ],
                        ...(nextIsAbandoned ? getAbandonedCartSchedulePatch(prev.filters) : {}),
                      };
                      if (nextIsAbandoned) {
                        setAbandonedScheduleDraft(syncAbandonedScheduleDraftFromFilters(nextFilters));
                      }
                      return {
                        ...prev,
                        filters: nextFilters,
                        actions: nextIsAbandoned
                          ? [{ type: "send_template", template_id: prev.actions?.[0]?.template_id || "", variable_paths: prev.actions?.[0]?.variable_paths || { header: {}, body: {} } }]
                          : prev.actions,
                      };
                    });
                  }}
                >
                  {eventOptions.map((ev) => (
                    <MenuItem key={ev.value} value={ev.value}>{ev.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isAbandonedEvent && (
                <>
                <TextField
                  label="Send first reminder after (minutes)"
                  type="text"
                  inputMode="numeric"
                  fullWidth
                  value={abandonedScheduleDraft.triggerDelayMinutes}
                  onChange={(e) => {
                    setAbandonedScheduleDraft((prev) => ({
                      ...prev,
                      triggerDelayMinutes: sanitizeNumericDraftInput(e.target.value),
                    }));
                  }}
                  helperText="Time after the last cart activity before the first reminder (5 min – 7 days)."
                  error={!!ruleErrors.filters}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={followUpEnabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setRuleForm((prev) => ({
                          ...prev,
                          filters: {
                            ...(prev.filters || { logic: "all", conditions: [] }),
                            trigger_delay_minutes: prev.filters?.trigger_delay_minutes ?? 60,
                            reminder_interval_minutes: enabled ? (prev.filters?.reminder_interval_minutes ?? 2880) : null,
                            max_reminder_days: enabled ? (prev.filters?.max_reminder_days ?? 7) : null,
                            max_reminder_count: enabled ? (prev.filters?.max_reminder_count ?? null) : null,
                          },
                        }));
                        if (enabled) {
                          setAbandonedScheduleDraft((prev) => ({
                            ...prev,
                            intervalDays: prev.intervalDays || "2",
                            maxReminderDays: prev.maxReminderDays || "7",
                          }));
                        }
                      }}
                    />
                  }
                  label="Send follow-up reminders"
                />
                {followUpEnabled && (
                  <>
                    <TextField
                      label="Repeat every (days)"
                      type="text"
                      inputMode="numeric"
                      fullWidth
                      value={abandonedScheduleDraft.intervalDays}
                      onChange={(e) => {
                        setAbandonedScheduleDraft((prev) => ({
                          ...prev,
                          intervalDays: sanitizeNumericDraftInput(e.target.value),
                        }));
                      }}
                      helperText="Gap between follow-up messages after the previous reminder (24 hours per day)."
                      error={!!ruleErrors.filters}
                    />
                    <TextField
                      label="Stop sending after (days)"
                      type="text"
                      inputMode="numeric"
                      fullWidth
                      value={abandonedScheduleDraft.maxReminderDays}
                      onChange={(e) => {
                        setAbandonedScheduleDraft((prev) => ({
                          ...prev,
                          maxReminderDays: sanitizeNumericDraftInput(e.target.value),
                        }));
                      }}
                      helperText={`Campaign window from when the cart sequence starts (max ${ABANDONED_CART_MAX_REMINDER_DAYS} days).`}
                      error={!!ruleErrors.filters}
                    />
                    <TextField
                      label="Max reminders (optional)"
                      type="text"
                      inputMode="numeric"
                      fullWidth
                      value={abandonedScheduleDraft.maxReminderCount}
                      onChange={(e) => {
                        setAbandonedScheduleDraft((prev) => ({
                          ...prev,
                          maxReminderCount: sanitizeNumericDraftInput(e.target.value),
                        }));
                      }}
                      helperText={`Leave empty to auto-calculate from interval and stop-after days. Up to ~${estimatedReminders} reminder(s) with current settings.`}
                      error={!!ruleErrors.filters}
                    />
                  </>
                )}
                </>
              )}
                  </>
                );
              })()}
            </>
          ) : isOrderReceivedTrigger(ruleForm.trigger_type) ? (
            <Typography variant="caption" color="text.secondary">
              Order trigger runs from inbound WhatsApp order payload. Keyword filters are not used.
            </Typography>
          ) : isImageReceivedTrigger(ruleForm.trigger_type) ? (
            <Typography variant="caption" color="text.secondary">
              Image trigger runs when the customer sends a photo. Keyword filters are not used.
            </Typography>
          ) : !isLeadStageFollowupTrigger(ruleForm.trigger_type) && !isPhoneCallAutomationTrigger(ruleForm.trigger_type) ? (
            <>
              <FormControl fullWidth>
                <InputLabel>Keyword filter (optional)</InputLabel>
                <Select
                  label="Keyword filter (optional)"
                  value={(ruleForm.filters?.conditions || []).some((c) => c.field === "keyword")
                    ? "enabled"
                    : ((ruleForm.filters?.conditions || []).some((c) => c.field === "first_text_no_keyword_today")
                      ? "no_keyword_daily"
                      : "disabled")}
                  onChange={(e) => {
                    if (e.target.value === "enabled") {
                      setRuleForm((prev) => ({
                        ...prev,
                        filters: {
                          logic: "all",
                          conditions: [{ field: "keyword", operator: "contains", value: "", match_method: "CONTAINS" }],
                        },
                      }));
                    } else if (e.target.value === "no_keyword_daily") {
                      setRuleForm((prev) => ({
                        ...prev,
                        filters: {
                          logic: "all",
                          conditions: [{ field: "first_text_no_keyword_today", operator: "equals", value: true }],
                        },
                      }));
                    } else {
                      setRuleForm((prev) => ({ ...prev, filters: { logic: "all", conditions: [] } }));
                    }
                  }}
                >
                  <MenuItem value="disabled">No keyword filter</MenuItem>
                  <MenuItem value="enabled">Match keyword</MenuItem>
                  <MenuItem value="no_keyword_daily">No-keyword fallback (first text/day)</MenuItem>
                </Select>
              </FormControl>
              {(ruleForm.filters?.conditions || []).some((c) => c.field === "keyword") && (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Match method</InputLabel>
                    <Select
                      label="Match method"
                      value={(ruleForm.filters?.conditions || []).find((c) => c.field === "keyword")?.match_method || "CONTAINS"}
                      onChange={(e) =>
                        setRuleForm((prev) => ({
                          ...prev,
                          filters: {
                            logic: "all",
                            conditions: [{
                              field: "keyword",
                              operator: "contains",
                              value: (prev.filters?.conditions || []).find((c) => c.field === "keyword")?.value || "",
                              match_method: e.target.value,
                            }],
                          },
                        }))
                      }
                    >
                      <MenuItem value="CONTAINS">Contains</MenuItem>
                      <MenuItem value="EXACT">Exact</MenuItem>
                      <MenuItem value="STARTS_WITH">Starts with</MenuItem>
                      <MenuItem value="ENDS_WITH">Ends with</MenuItem>
                    </Select>
                  </FormControl>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Keywords (any match fires the rule; same match method for all)
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.75,
                        alignItems: "center",
                        minHeight: 36,
                        p: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: ruleErrors.filters ? "error.main" : "divider",
                        mb: 1,
                      }}
                    >
                      {parseRuleKeywordList(
                        (ruleForm.filters?.conditions || []).find((c) => c.field === "keyword")?.value || "",
                      ).map((kw, idx, list) => (
                        <Chip
                          key={`${kw}-${idx}`}
                          size="small"
                          label={kw}
                          onDelete={() => {
                            const next = list.filter((_, i) => i !== idx);
                            const mm =
                              (ruleForm.filters?.conditions || []).find((c) => c.field === "keyword")?.match_method
                              || "CONTAINS";
                            setRuleForm((prev) => ({
                              ...prev,
                              filters: {
                                logic: "all",
                                conditions: [
                                  {
                                    field: "keyword",
                                    operator: "contains",
                                    value: joinRuleKeywordList(next),
                                    match_method: mm,
                                  },
                                ],
                              },
                            }));
                          }}
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <TextField
                        size="small"
                        label="Add keywords"
                        placeholder="e.g. hi or sale, offer — press Enter or Add"
                        value={ruleKeywordDraft}
                        onChange={(e) => setRuleKeywordDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitRuleKeywordDraft();
                          }
                        }}
                        fullWidth
                        error={!!ruleErrors.filters}
                        helperText={ruleErrors.filters}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mt: 0.5, flexShrink: 0 }}
                        onClick={() => commitRuleKeywordDraft()}
                      >
                        Add
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRuleDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRule}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isRuleDeleteDialogOpen}
        onClose={() => {
          setIsRuleDeleteDialogOpen(false);
          setRuleToDelete(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete rule</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete{" "}
            <strong>{ruleToDelete?.rule_name || "this rule"}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsRuleDeleteDialogOpen(false);
              setRuleToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteRule}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isFormDeleteDialogOpen}
        onClose={() => {
          setIsFormDeleteDialogOpen(false);
          setFormToDelete(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete form</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete{" "}
            <strong>{formToDelete?.name || "this form"}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsFormDeleteDialogOpen(false);
              setFormToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteForm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Automation Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          setSelectedAutomation(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutomationIcon sx={{ color: "#1976d2" }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedAutomation ? "Edit Automation" : "Create New Automation"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              label="Automation Name"
              placeholder="Enter automation name"
              fullWidth
              defaultValue={selectedAutomation?.name || ""}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              label="Description"
              placeholder="Describe what this automation does"
              multiline
              rows={3}
              fullWidth
              defaultValue={selectedAutomation?.description || ""}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Trigger Type</InputLabel>
              <Select
                label="Trigger Type"
                defaultValue={selectedAutomation?.trigger || ""}
                sx={{
                  borderRadius: 2,
                }}
              >
                <MenuItem value="New Contact">New Contact</MenuItem>
                <MenuItem value="Tag Added">Tag Added</MenuItem>
                <MenuItem value="Date">Date</MenuItem>
                <MenuItem value="Event">Event</MenuItem>
                <MenuItem value="Message Received">Message Received</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Action Type</InputLabel>
              <Select
                label="Action Type"
                defaultValue={selectedAutomation?.action || ""}
                sx={{
                  borderRadius: 2,
                }}
              >
                <MenuItem value="Send Message">Send Message</MenuItem>
                <MenuItem value="Message Sequence">Message Sequence</MenuItem>
                <MenuItem value="Add Tag">Add Tag</MenuItem>
                <MenuItem value="Remove Tag">Remove Tag</MenuItem>
                <MenuItem value="Update Contact">Update Contact</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  defaultChecked={selectedAutomation?.status === "active"}
                />
              }
              label="Activate automation immediately"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => {
              setIsCreateDialogOpen(false);
              setSelectedAutomation(null);
            }}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" },
              borderRadius: 2,
              px: 3,
            }}
          >
            {selectedAutomation ? "Update Automation" : "Create Automation"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* No-keyword Fallback Dialog */}
      <Dialog
        open={isFallbackDialogOpen}
        onClose={() => setIsFallbackDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontSize: "20px",
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SmartToyOutlinedIcon sx={{ color: "#1976d2" }} />
            No-keyword fallback
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If the first inbound text of the day doesn&apos;t match any keyword, this flow triggers once per phone per day.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={noKeywordFallbackConfig.enabled}
                  onChange={(e) => setNoKeywordFallbackConfig((prev) => ({
                    ...prev,
                    enabled: e.target.checked,
                  }))}
                />
              }
              label="Enable no-keyword fallback"
            />

            <FormControl fullWidth disabled={!noKeywordFallbackConfig.enabled}>
              <InputLabel>Select Fallback Chatbot</InputLabel>
              <Select
                label="Select Fallback Chatbot"
                value={noKeywordFallbackConfig.fallbackChatbotId || ""}
                onChange={(e) => setNoKeywordFallbackConfig((prev) => ({
                  ...prev,
                  fallbackChatbotId: e.target.value,
                }))}
                sx={{ borderRadius: 2 }}
              >
                {chatbots.map((bot) => (
                  <MenuItem key={bot.id} value={bot.id}>
                    {bot.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setIsFallbackDialogOpen(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveNoKeywordFallbackConfig}
            disabled={!noKeywordFallbackConfig.enabled}
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" },
              borderRadius: 2,
              px: 3,
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Keyword Action Dialog */}
      <Dialog
        open={isAddKeywordDialogOpen}
        onClose={handleCloseAddKeywordDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontSize: "20px",
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          Add Keyword Action
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              label="Keyword"
              placeholder="Enter keyword (e.g., 'help', 'pricing')"
              fullWidth
              value={keywordAction.keyword}
              onChange={(e) =>
                handleKeywordActionChange("keyword", e.target.value)
              }
              error={!!keywordActionErrors.keyword}
              helperText={keywordActionErrors.keyword}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            {/* Matching Method Dropdown */}
            <FormControl fullWidth>
              <InputLabel>Matching Method</InputLabel>
              <Select
                label="Matching Method"
                value={keywordAction.matchingMethod}
                onChange={(e) => handleKeywordActionChange("matchingMethod", e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="EXACT">Exact</MenuItem>
                <MenuItem value="CONTAINS">Contains</MenuItem>
                <MenuItem value="STARTS_WITH">Starts With</MenuItem>
                <MenuItem value="ENDS_WITH">Ends With</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth error={!!keywordActionErrors.chatbot}>
              <InputLabel>Select Chatbot</InputLabel>
              <Select
                label="Select Chatbot"
                value={keywordAction.chatbot}
                onChange={(e) => {
                  console.log('Selected value from dropdown:', e.target.value);

                  const selectedChatbot = chatbots.find(c => c.name === e.target.value);
                  if (selectedChatbot) {

                    handleKeywordActionChange("chatbot", e.target.value);
                    handleKeywordActionChange("chatbotId", selectedChatbot.id);
                    console.log('After update - chatbot:', e.target.value);
                    console.log('After update - chatbotId:', selectedChatbot.id);
                  } else {
                    console.log('No chatbot found with name:', e.target.value);
                  }
                }}
              >
                {chatbots.map((chatbot) => (
                  <MenuItem key={chatbot.id} value={chatbot.name}>
                    {chatbot.name}
                  </MenuItem>
                ))}
              </Select>
              {keywordActionErrors.chatbot && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {keywordActionErrors.chatbot}
                </Typography>
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCloseAddKeywordDialog}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveKeywordAction}
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" },
              borderRadius: 2,
              px: 3,
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Chatbot Dialog */}
      <Dialog
        open={isCreateChatbotDialogOpen}
        onClose={handleCloseCreateChatbotDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontSize: "20px",
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          Create Chatbot
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              label="Chatbot Name"
              placeholder="Enter chatbot name (e.g., 'Customer Support Bot')"
              fullWidth
              value={newChatbot.name}
              onChange={(e) => handleChatbotNameChange(e.target.value)}
              error={!!chatbotErrors.name}
              helperText={chatbotErrors.name}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCloseCreateChatbotDialog}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateChatbot}
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" },
              borderRadius: 2,
              px: 3,
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Configure Form Dialog */}
      <Dialog
        open={isFormDialogOpen}
        onClose={handleCloseFormDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
            minHeight: "85vh",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <FormFlowBuilder
            open={isFormDialogOpen}
            initialFormId={selectedFormId}
            onClose={handleCloseFormDialog}
            onSaved={() => fetch()}
          />
        </DialogContent>
      </Dialog>

      {snackBar()}
    </Box>
  );
};

export default AutomationView;
